// src/components/Layout/Sidebar.tsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { BRAND_NAME } from '../config/env';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { PERMISSION_PAR_ROUTE } from '../utils/permissionsParRoute';
import { useApp } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import SidebarDynamicTypes from './SidebarDynamicTypes';
import SidebarAutoModule from './SidebarAutoModule';
import { getModulesCatalogueFull, ModuleCatalogueDB } from '../services/package.service';
import { IS_CGEGUI } from '../data/tenantFlag';
// Note: Inbox est déjà importé dans le bloc principal lucide-react ci-dessous,
// on n'ajoute donc QUE Mail et Send ici (Send peut aussi déjà être présent — vérifié).
import { Mail, Send, Leaf, HeartPulse, FolderCheck
} from 'lucide-react';
// ArrowLeftRight : entrée « Cours de devises » du menu Paramétrage.
// Importé ici plutôt que dans le bloc principal ci-dessous, qui est trié et
// déjà très long — l'ajout reste ainsi lisible en revue.
import { ArrowLeftRight } from 'lucide-react';
import {
  LayoutDashboard,
  ShoppingCart,
  ShoppingBag,
  Package,
  ClipboardList,
  Gauge,
  Milestone,
  MapPin,
  Landmark,
  PiggyBank,
  Banknote,
  Settings,
  BarChart3,
  Bell,
  Eye,
  CreditCard,
  Wallet,
  Receipt,
  X,
  Edit3,
  Menu,
  Tag,
  Gift,
  TicketPercent,
  FileSpreadsheet,
  User as UserIcon,
  LogOut,
  Users,
  UserPlus,
  Contact2,
  Plus,
  Building2,
  Briefcase,
  FileText,
  DollarSign,
  History,
  CheckCircle,
  Clock,
  Filter,
  Download,
  Printer,
  Trash2,
  Archive,
  RefreshCw,
  AlertCircle,
  Home,
  Calendar,
  CalendarRange,
  User,
  Key,
  Shield,
  Link as LinkIcon,
  Store,
  Wrench,
  HandCoins,
  Map as MapIcon,
  Lock,
  Truck,
  Package as PackageIcon,
  Percent,
  CreditCard as CreditCardIcon,
  ChevronDown,
  ChevronRight,
  BookOpen,
  TrendingUp,
  ClipboardCheck,
  Inbox,
  UserX,
  GraduationCap,
  Star,
  Heart,
  LogOutIcon,
  Activity,
  PieChart,
  Monitor,
  Landmark as LandmarkIcon,
  Globe,
  Boxes,
  FileBarChart,
  ScanLine,
  ShieldCheck,
  HardHat,
  ShieldAlert,
  Scissors,
  Radar,
  FileCheck,
  Coins,
  Database,
  Code,
  Target,
  Smartphone,
  Upload,
  Crown,
  Layers,
  ArrowRightLeft,
  Ban,
  Plug,
  Calculator,
  Zap,
  FileSignature,
  Network,
  Handshake,
  MessageSquareWarning,
  Smile,
  List,
  Search,
  Car,
  Grid3x3,
  CalendarCheck,
  Award,
  LayoutGrid,
  PartyPopper,
  BarChart2,
  Stethoscope,
  CalendarOff,
  BookUser,
  GitBranch,
  FolderKanban,
} from 'lucide-react';

// Sprint EEC — l'ensemble des droits liés au module Électricité.
// Le menu de premier niveau « Électricité » est visible dès que l'utilisateur
// détient AU MOINS UN de ces droits. Les admins/super_admins voient tout via le
// bypass de hasPermission. Chaque entrée du sous-menu reste gardée par son droit
// dédié (cf. rendu du sous-menu `electricite`).
const UTIL_PERMISSIONS = [
  'access_facturation_electrique',
  'gerer_tarifs_electriques',
  'gerer_regroupements',
  'acces_releve_terrain',
  'acces_intervention_terrain',
  // autres droits électricité (visibilité du menu seulement) :
  'gerer_campagne_facturation',
  'gerer_lots_releve',
  'editer_bordereau_releve',
  'saisir_releve_index',
  'controler_releve',
  'gerer_enquete_releve',
  'calculer_facture_electrique',
  'verifier_facture_electrique',
  'emettre_facture_electrique',
  'valider_facture_electrique',
  'imprimer_facture_electrique',
  'distribuer_facture_electrique',
  'voir_bilan_cycle',
];

// ── Super-blocs de 1er niveau (encapsulation du menu plat) ─────────────────
// On regroupe les items existants (`menuItems[].id`) sous des super-blocs
// collapsibles SANS toucher aux routes/permissions ni aux sous-menus.
// `dashboard` (haut) et `monEspace` (bas) restent épinglés HORS bloc.
// Les modules BD `autoModules` (Patronat) sont rendus dans le bloc ERP.
// NB : « Sécurité & Supervision » contient `alertes` et `supervision`
// (l'écran Supervision / Piste d'audit est désormais une entrée propre de ce
// bloc, et n'est plus dupliqué en sous-item d'Administration ni de Reporting).
interface SuperBloc {
  id: string;
  label: string;
  icon: any;
  itemIds: string[];
}

// Forme permissive d'un item de menu : évite que l'inférence littérale du
// tableau `menuItems` crée une union de formes exactes (badge/module tantôt
// requis tantôt absents) incompatible avec les items passés à renderMenuItem.
interface MenuItemDef {
  id: string;
  label: string;
  icon: any;
  permission: string;
  /**
   * Droits qui ouvrent l'entrée à défaut du droit principal.
   *
   * Un bloc de menu peut abriter un module dont les titulaires n'ont pas — et
   * ne doivent pas avoir — le droit général du bloc : le médecin du travail
   * n'a rien à faire dans les dossiers du personnel, mais son écran vit dans
   * le bloc RH.
   */
  permissionsAlt?: string[];
  path: string;
  badge?: string;
  module?: string;
}

const SUPER_BLOCS: SuperBloc[] = [
  {
    id: 'administration',
    label: 'Administration',
    icon: ShieldCheck,
    itemIds: ['admin'],
  },
  {
    id: 'erp',
    label: 'ERP',
    icon: Boxes,
    // Les modules ERP (caisse, stock, achats, comptabilite, finances, vie scolaire,
    // courrier, dossier electronique, etc.) sont désormais rendus dynamiquement via
    // SidebarAutoModule (autoSidebar=true dans le catalogue BD). Les outils
    // système non-catalogue restent ici : societe, immobilisations.
    // RH est géré via renderMenuItem (accordion admin) et exclu de erpAutoModules.
    itemIds: ['rh', 'societe', 'immobilisations', 'plantation'],
  },
  {
    id: 'crm',
    label: 'CRM',
    icon: TrendingUp,
    itemIds: ['demandes_client', 'enquetes_satisfaction', 'demandesRemboursement'],
  },
  {
    id: 'interfaces',
    label: 'Interfaces & utilitaires',
    icon: Plug,
    itemIds: ['apiZone', 'bridge'],
  },
  {
    id: 'reporting',
    label: 'Reporting & BI',
    icon: BarChart3,
    itemIds: ['reporting', 'pilotage'],
  },
  {
    id: 'securite',
    label: 'Sécurité & Supervision',
    icon: Eye,
    itemIds: [],
  },
  {
    id: 'consolidation',
    label: 'Consolidation Groupe',
    icon: Network,
    itemIds: ['consolidation', 'consolidationElectricite'],
  },
];

// Index transversal du reporting : liens vers les hubs « États » de chaque
// module. Piloté 100% par configuration — AUCUN lien en dur. Chaque entrée est
// gardée par la MÊME permission que sa route cible (cf. requiredPermission dans
// AppRoutes.tsx) pour que la visibilité corresponde à l'accès. hasPermission
// bypasse déjà pour le super_admin → il voit tout.
interface EtatDomaineDef {
  label: string;
  icon: any;
  permission: string;
  path: string;
  // true → état actif via location.pathname.startsWith(base) (pages à sous-routes).
  // Par défaut (false/absent) → égalité stricte sur le pathname.
  matchPrefix?: boolean;
}

const ETATS_PAR_DOMAINE: EtatDomaineDef[] = [
  { label: "Édition (constructeur d'états)", icon: Printer, permission: 'access_edition', path: '/edition' },
  { label: 'États comptables', icon: Landmark, permission: 'access_comptabilite', path: '/comptabilite?tab=etats' },
  { label: 'États financiers & trésorerie', icon: Wallet, permission: 'access_finances', path: '/finances?tab=etats' },
  { label: 'Recouvrement & créances', icon: AlertCircle, permission: 'access_facturation_electrique', path: '/recouvrement' },
  { label: 'Accords commerciaux', icon: Handshake, permission: 'access_recouvrement', path: '/accords-commerciaux' },
  { label: 'États CRM / commercial', icon: Contact2, permission: 'voir_etats_crm', path: '/crm/etats' },
  { label: 'États RH & Paie', icon: Users, permission: 'access_rh', path: '/rh/etats' },
  { label: 'États Achats', icon: ShoppingBag, permission: 'access_achats', path: '/achats?tab=etats' },
  { label: 'États Immobilier', icon: Building2, permission: 'access_immobilier', path: '/immobilier?tab=etats' },
  { label: 'États Électricité', icon: Zap, permission: 'access_consolidation_electricite', path: '/consolidation-electricite' },
  { label: 'Consolidation Groupe', icon: Layers, permission: 'access_consolidation_groupe', path: '/consolidation' },
];

// Le bloc ERP héberge aussi les modules BD auto-sidebar (Patronat).
const ERP_BLOC_ID = 'erp';

/**
 * Lien de menu qui se masque quand l'utilisateur n'a pas le droit d'ouvrir la
 * page visee.
 *
 * La permission n'est pas redeclaree ici : elle est lue dans la table derivee
 * des routes (PERMISSION_PAR_ROUTE). Sans cela, chaque nouvelle entree de menu
 * exige de penser a repeter le droit de la route — un oubli invisible en
 * developpement, qui ne se manifeste que chez l'utilisateur restreint, sous la
 * forme d'un ecran « Acces non autorise » atteint depuis son propre menu.
 *
 * Une destination absente de la table (route publique, ou protegee autrement
 * que par `requiredPermission`) est affichee : on ne masque que ce dont on
 * sait avec certitude qu'il sera refuse.
 */
const LienMenu: React.FC<React.ComponentProps<typeof Link>> = ({ to, children, ...reste }) => {
  const { hasPermission } = useAuth();
  const cible = typeof to === 'string' ? to : '';
  const requise = PERMISSION_PAR_ROUTE[cible];
  if (requise && !hasPermission(requise)) return null;
  return (
    <Link to={to} {...reste}>
      {children}
    </Link>
  );
};

const Sidebar: React.FC = () => {
  const { sidebarOpen, setSidebarOpen } = useApp();
  const { hasPermission, hasModule, user, permissionsLoaded, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  // Compteur d'alertes RH
  const [alertesRHCount, setAlertesRHCount] = useState(0);

  /**
   * Referme la barre UNIQUEMENT lorsqu'elle se comporte en tiroir superposé.
   *
   * Le seuil doit rester identique au point de rupture des classes CSS
   * ci-dessous (`md:static`, `md:translate-x-0`) : au-dessus, la barre est
   * permanente et la refermer la réduirait à une colonne d'icônes — c'est
   * exactement le « le menu se ferme tout seul » constaté au moindre clic.
   *
   * Le test était auparavant écrit en toutes lettres dans 172 gestionnaires,
   * sur le seuil `lg` (1024 px). Une fenêtre d'ordinateur portable, ou un zoom
   * navigateur au-delà de 100 %, passait donc sous la barre et faisait
   * basculer l'interface en mode mobile sans que rien ne le laisse deviner.
   *
   * `matchMedia` est interrogé à l'instant du clic : il suit le
   * redimensionnement et le zoom sans qu'aucun état ne soit à maintenir.
   */
  const fermerSiDrawer = useCallback(() => {
    if (typeof window === 'undefined') return;
    if (window.matchMedia('(max-width: 767px)').matches) {
      setSidebarOpen(false);
    }
  }, [setSidebarOpen]);

  // MOB-4 — Auto-close du drawer mobile à chaque changement de route.
  // Filet de sécurité au cas où un lien interne ne déclenche pas la fermeture
  // (navigation programmatique, lien custom dans un sous-composant…).
  useEffect(() => {
    fermerSiDrawer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Mai 2026 — Badge "Rapports à valider" : nombre de rapports périodiques
  // soumis ET ciblés sur l'utilisateur courant (destinataireUserId=moi ou
  // destinataireProfilId=mon profil). Rafraîchi toutes les 60s.
  const [rapportsAValiderCount, setRapportsAValiderCount] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const charger = async () => {
      try {
        const mod = await import('../services/rapportPeriodique.service');
        const data = await mod.rapportPeriodiqueService.aValider();
        if (!cancelled) setRapportsAValiderCount(data.length);
      } catch {
        if (!cancelled) setRapportsAValiderCount(0);
      }
    };
    if (user) {
      charger();
      const interval = setInterval(charger, 60_000);
      return () => { cancelled = true; clearInterval(interval); };
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Mai 2026 — MM-17 : Badge "Mes tâches" — nombre de demandes en attente
  // d'action sur les étapes workflow où le profil de l'utilisateur est acteur.
  // Rafraîchi toutes les 60s.
  const [mesTachesCount, setMesTachesCount] = useState({ total: 0, enRetard: 0 });
  useEffect(() => {
    let cancelled = false;
    const charger = async () => {
      try {
        const mod = await import('../services/mesTaches.service');
        const data = await mod.mesTachesService.count();
        if (!cancelled) setMesTachesCount(data);
      } catch {
        if (!cancelled) setMesTachesCount({ total: 0, enRetard: 0 });
      }
    };
    if (user) {
      charger();
      const interval = setInterval(charger, 60_000);
      return () => { cancelled = true; clearInterval(interval); };
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Affichage conditionnel de "Tous les courriers" : réservé à SG, SGA, Présidents.
  // Les autres utilisateurs voient leurs courriers via "Mes demandes" / "Tableau des taches"
  // et la liste backend est de toute façon filtrée pour eux.
  const canViewAllCourriers = useMemo(() => {
    if (!user) return false;
    if (hasPermission('view_all_courriers')) return true;
    // Fallback transitoire le temps que le droit `view_all_courriers` soit
    // poussé sur tous les profils SG/SGA/Présidents en prod.
    // TODO: retirer ce fallback une fois la propagation effectuée.
    const profilLower = (user.profilNom || '').toLowerCase();
    return /secr[ée]taire g[ée]n[ée]ral|pr[ée]sident/.test(profilLower);
  }, [user, hasPermission, permissionsLoaded]);

  useEffect(() => {
    if (!hasModule('rh') || !hasPermission('access_rh') || !(user?.scopeEntiteId ?? user?.entiteId)) return;
    const loadAlertesCount = async () => {
      try {
        const { rhService } = await import('../services/rhService');
        const [contrats, conges, absences, demissions] = await Promise.allSettled([
          rhService.getContrats({ entiteId: (user.scopeEntiteId ?? user.entiteId)! }),
          rhService.getDemandesConge({ entiteId: (user.scopeEntiteId ?? user.entiteId)! }),
          rhService.getDemandesAbsence({ entiteId: (user.scopeEntiteId ?? user.entiteId)! }),
          rhService.getDemissions(),
        ]);
        let count = 0;
        const today = new Date().toISOString().split('T')[0];
        const in30 = new Date(); in30.setDate(in30.getDate() + 30);
        const in30Str = in30.toISOString().split('T')[0];
        if (contrats.status === 'fulfilled') {
          count += (contrats.value as any[]).filter((c: any) => c.statut === 'actif' && c.dateFin && c.dateFin <= in30Str).length;
        }
        if (conges.status === 'fulfilled') count += (conges.value as any[]).filter((c: any) => c.statut === 'en_attente').length;
        if (absences.status === 'fulfilled') count += (absences.value as any[]).filter((a: any) => a.statut === 'en_attente').length;
        if (demissions.status === 'fulfilled') count += (demissions.value as any[]).filter((d: any) => ['declaree', 'en_negociation', 'en_cours_preavis'].includes(d.statut)).length;
        setAlertesRHCount(count);
      } catch { /* silencieux */ }
    };
    loadAlertesCount();
    const interval = setInterval(loadAlertesCount, 5 * 60 * 1000); // Rafraîchir toutes les 5 min
    return () => clearInterval(interval);
  }, [(user?.scopeEntiteId ?? user?.entiteId)]); // eslint-disable-line

  // États pour les sous-menus ouverts/fermés
  const [openSubmenus, setOpenSubmenus] = useState<Record<string, boolean>>({
    administration: false,
    monEspace: true,
    rh: true,
  });

  // ── État déplié/replié des super-blocs de 1er niveau ──────────────────────
  // Persisté en localStorage (clé `sidebar_open_bloc_<id>`), comme
  // SidebarAutoModule. Par défaut tous ouverts (la valeur initiale n'est pas
  // false explicitement → un bloc sans préférence stockée est considéré ouvert).
  const [openBlocs, setOpenBlocs] = useState<Record<string, boolean>>(() => {
    const init: Record<string, boolean> = {};
    SUPER_BLOCS.forEach(b => { init[b.id] = true; });
    return init;
  });

  const toggleBloc = (blocId: string) => {
    setOpenBlocs(prev => {
      const next = { ...prev, [blocId]: !prev[blocId] };
      try {
        localStorage.setItem(`sidebar_open_bloc_${blocId}`, String(next[blocId]));
      } catch { /* quota / mode privé : silencieux */ }
      return next;
    });
  };

  // Accordéon des sections Mon Espace — persisté en localStorage
  const [meEspaceOpen, setMeEspaceOpen] = React.useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem('sidebar_monespace_sections');
      const defaults = { tableaux: true, carriere: false, demandes: true, pilotage: false, documents: false, vie: true, commInterneEspace: true };
      return saved ? { ...defaults, ...JSON.parse(saved) } : defaults;
    } catch { return { tableaux: true, carriere: false, demandes: true, pilotage: false, documents: false, vie: true, commInterneEspace: true }; }
  });
  const toggleMeEspace = (key: string) => {
    setMeEspaceOpen(prev => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem('sidebar_monespace_sections', JSON.stringify(next)); } catch {}
      return next;
    });
  };

  // Accordéon des sections RH admin — persisté en localStorage
  const [rhOpen, setRhOpen] = React.useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem('sidebar_rh_sections');
      const defaults = {
        capital: true, paie: false, presence: false, formation: false,
        disciplinaire: false, etats: false, medecine: true, commInterne: false,
        relationsSociales: false, docsEntreprise: false,
      };
      return saved ? { ...defaults, ...JSON.parse(saved) } : defaults;
    } catch {
      return {
        capital: true, paie: false, presence: false, formation: false,
        disciplinaire: false, etats: false, medecine: true, commInterne: false,
        relationsSociales: false, docsEntreprise: false,
      };
    }
  });
  const toggleRh = (key: string) => {
    setRhOpen(prev => {
      const next = { ...prev, [key]: !prev[key] };
      try { localStorage.setItem('sidebar_rh_sections', JSON.stringify(next)); } catch {}
      return next;
    });
  };

  const [filteredMenuItems, setFilteredMenuItems] = useState<MenuItemDef[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  // ── Catalogue des modules avec autoSidebar=true ─────────────────────────
  // Chargé une seule fois après login pour générer dynamiquement les items
  // de menu Patronat (gestion_adherents, gestion_activites, secretariat_general)
  // sans qu'aucun code React ne soit dédié à un module en particulier.
  const [autoModules, setAutoModules] = useState<ModuleCatalogueDB[]>([]);
  useEffect(() => {
    if (!user) return;
    let alive = true;
    getModulesCatalogueFull()
      .then(all => {
        if (!alive) return;
        setAutoModules(all.filter(m => m.autoSidebar && m.actif));
      })
      .catch(() => {/* fallback silencieux */});
    return () => { alive = false; };
  }, [user]);

  const menuItems: MenuItemDef[] = [
    // Toujours visibles (pas de module requis)
    {
      id: 'dashboard',
      label: t('nav.dashboard'),
      icon: LayoutDashboard,
      permission: 'access_dashboard',
      path: '/dashboard'
    },
    {
      id: 'admin',
      label: t('nav.administration'),
      icon: Settings,
      permission: 'access_admin',
      path: '/administration'
    },
    {
      id: 'alertes',
      label: t('nav.alertes'),
      icon: Bell,
      permission: 'access_alertes',
      path: '/alertes'
    },
    {
      id: 'supervision',
      label: 'Supervision & Piste d\'audit',
      icon: Eye,
      permission: 'access_supervision',
      module: 'supervision',
      path: '/supervision'
    },
    {
      id: 'dbSync',
      label: 'Sync Base de Données',
      icon: RefreshCw,
      permission: 'access_db_sync',
      path: '/db-sync'
    },
    {
      id: 'apiZone',
      label: t('nav.apiZone'),
      icon: Globe,
      permission: 'access_admin',
      path: '/api-zone'
    },
    // Interfaçage cross-app (Bridge) — tokens M2M + webhooks vers SIGP, eHonneur…
    // Gardé par manage_bridge (super_admin par bypass). Rendu comme entrée propre
    // dans le super-bloc « Interfaces & utilitaires ».
    {
      id: 'bridge',
      label: 'Interfaçage système tiers',
      icon: Plug,
      permission: 'access_interfacage',
      path: '/admin/bridge'
    },
    // Reporting & BI — hub des états par domaine + statistiques + bilan périodique.
    {
      id: 'reporting',
      label: 'Reporting & BI',
      icon: BarChart3,
      permission: 'access_statistiques',
      path: '/statistiques',
    },
    // Centre de Pilotage Stratégique — vue de gouvernance consolidée (KPI,
    // alertes, anomalies + accès aux centres d'analyse). Réservé via
    // access_pilotage (super_admin par bypass).
    {
      id: 'pilotage',
      label: 'Centre de Pilotage',
      icon: Gauge,
      permission: 'access_pilotage',
      path: '/pilotage',
    },
    // Fonds d épargne à adhérents — registre des souscripteurs, lettrage des
    // flux bancaires, séquestre et capitalisation. Réservé via
    // access_fonds_epargne (super_admin par bypass).
    {
      id: 'fondsEpargne',
      label: "Fonds d'épargne",
      icon: PiggyBank,
      permission: 'access_fonds_epargne',
      path: '/fonds-epargne',
    },
    // Module Consolidation Groupe — états financiers consolidés multi-entités.
    // Réservé via access_consolidation_groupe (super_admin par bypass).
    {
      id: 'consolidation',
      label: 'Consolidation générale',
      icon: Network,
      permission: 'access_consolidation_groupe',
      path: '/consolidation',
    },
    // Consolidation Électricité — tableaux consolidés EEC multi-DR/agences.
    {
      id: 'consolidationElectricite',
      label: 'Consolidation électricité',
      icon: Zap,
      permission: 'access_consolidation_electricite',
      path: '/consolidation-electricite',
    },
    // Demandes de Remboursement — module électricité EDG.
    {
      id: 'demandesRemboursement',
      label: 'Remboursements',
      icon: RefreshCw,
      permission: 'access_demande_remboursement',
      path: '/demandes-remboursement',
      module: 'electricite',
    },
    // Module Édition (report-builder) — constructeur d'éditions dynamiques.
    // Réservé via access_edition (écriture des modèles : creer_modele_edition).
    {
      id: 'edition',
      label: t('nav.edition'),
      icon: Printer,
      permission: 'access_edition',
      path: '/edition',
    },
    // Admin des niveaux d'organisation (échelons DG→DC→DR→Agence).
    // Réservé via gerer_niveaux_organisation.
    {
      id: 'niveauxOrganisation',
      label: "Niveaux d'organisation",
      icon: Layers,
      permission: 'gerer_niveaux_organisation',
      path: '/niveaux-organisation',
    },
    // Module Société / Associés / Dividendes — gestion juridique de l'entité
    // (capital social, actionnariat, AG, distribution de dividendes).
    {
      id: 'societe',
      label: 'Société',
      icon: Building2,
      permission: 'view_associes',
      path: '/societe/associes',
    },
    // Module immobilisations (actifs amortissables : matériel, véhicules...)
    {
      id: 'immobilisations',
      label: 'Immobilisations',
      icon: Boxes,
      permission: 'access_immobilisations',
      path: '/immobilisations',
      module: 'immobilisations',
    },
    // MM-50 — Module Demandes Client (catalogue back-office des processus
    // EEC/SEG/... exposables côté abonné). Liste les types d'opération
    // ayant au moins une étape saisieEnLigne=true, scopés par entité.
    {
      id: 'demandes_client',
      label: 'Processus Métier',
      icon: FileText,
      // Point d'entrée pour DÉMARRER une demande : ouvert à tout utilisateur
      // connecté. Le paramétrage (Configurer / Aperçu) reste réservé à
      // `access_admin` dans la page elle-même.
      permission: 'access_processus_metier',
      path: '/admin/demandes-client',
      module: 'espace_client',
      badge: 'Nouveau',
    },
    {
      id: 'enquetes_satisfaction',
      label: 'Enquêtes satisfaction',
      icon: Smile,
      permission: 'access_demandes_client',
      path: '/crm/enquetes-satisfaction',
      module: 'electricite',
    },
    // Module RH — gestion du capital humain (admin RH)
    {
      id: 'rh',
      label: 'Capital Humain',
      icon: Users,
      permission: 'access_rh',
      /**
       * Droits qui ouvrent le bloc RH sans donner l'accès RH général.
       *
       * Un médecin du travail n'a rien à faire dans les dossiers du personnel —
       * il n'a donc pas `access_rh` — mais son module vit dans ce bloc. Sans
       * ces alternatives, il ne pouvait atteindre par le menu le seul écran
       * qui le concerne. Les groupes du bloc restent filtrés par leurs propres
       * droits : il n'y verra que ce qui lui revient.
       */
      permissionsAlt: [
        'acces_medecine_travail', 'gerer_sst', 'declarer_accident_travail',
        'declarer_conflit_interet',
      ],
      path: '/rh/dashboard',
      module: 'rh',
    },
    // Espace employé — accessible à tout utilisateur connecté
    {
      id: 'monEspace',
      label: t('nav.monEspace'),
      icon: User,
      permission: 'authenticated',
      path: '/rh/conges',
      module: 'authenticated'
    },
    // Module Plantation — gestion agro-industrielle (palmier à huile, hévéa, cacao)
    {
      id: 'plantation',
      label: 'Plantation',
      icon: Leaf,
      permission: 'access_plantation',
      path: '/plantation',
      module: 'plantation',
    },
    // Module Mode Couture — estimation IA des mesures de couture (écran couturier)
    {
      id: 'modeCouture',
      label: 'Mode Couture',
      icon: Scissors,
      permission: 'access_mode_couture',
      path: '/mode-couture',
    },
    // NB : les modules (caisse, stock, achats, RH, comptabilite, finances, CRM,
    // vie scolaire, electricite, courrier, dossier electronique, etc.) sont rendus
    // dynamiquement via SidebarAutoModule (autoSidebar=true dans le catalogue BD).
    // Pour ajouter un module au menu : INSERT dans le catalogue avec autoSidebar=true
    // + routeBase, puis créer des types d'opération via DevMode.
    // NB : les modules Patronat (gestion_adherents, gestion_activites,
    // secretariat_general) ne sont PAS hardcodés ici — ils sont rendus
    // automatiquement plus bas via <SidebarAutoModule> en lisant le catalogue
    // BD (ecom_modules_catalogue.autoSidebar = true). Pour ajouter un nouveau
    // module au menu : INSERT dans le catalogue avec autoSidebar=true +
    // routeBase, puis créer des types d'opération via DevMode.
  ];

  // Effet pour calculer les éléments du menu
  useEffect(() => {
    if (!permissionsLoaded) {
      // Pendant le chargement on montre Mon espace (accessible a tous)
      // au lieu du dashboard (qui necessite access_dashboard).
      setFilteredMenuItems(menuItems.filter(item => item.id === 'monEspace'));
      return;
    }

    const filtered = menuItems.filter(item => {
      // « Processus Métier » = point d'entrée des demandes client (branchement,
      // abonnement, et tous les processus métier exposables). Visible si
      // l'entité a le module espace_client OU electricite (le métier EEC inclut
      // la création des demandes, pas seulement la facturation).
      if (item.id === 'demandes_client') {
        // CGE-GUI (patronat) n'a pas de processus métier exposables : ses seuls
        // processus sont l'adhésion, le courrier et les activités, qui ont déjà
        // leurs propres entrées. L'entrée est donc retirée de la liste pour ce
        // tenant — rien n'est désactivé ni supprimé côté données.
        if (IS_CGEGUI) return false;
        return hasPermission(item.permission)
          && (hasModule('espace_client') || hasModule('electricite'));
      }
      const hasPerm = hasPermission(item.permission)
        || (item.permissionsAlt || []).some((p) => hasPermission(p));
      const hasModuleAccess = !item.module || hasModule(item.module);
      return hasPerm && hasModuleAccess;
    });

    setFilteredMenuItems(filtered);
  }, [permissionsLoaded, hasPermission, hasModule, user]);

  // Fonction pour vérifier si un élément de menu est actif
  const isMenuItemActive = (path: string) => {
    if (path === '/statistiques') {
      // Reporting & BI group: active when any sub-page is active
      return location.pathname === '/statistiques' ||
        location.pathname === '/bilan-periodique' ||
        location.pathname === '/supervision';
    }
    if (path === '/crm') {
      return location.pathname.startsWith('/crm') ||
        location.pathname.startsWith('/promotions') ||
        location.pathname.startsWith('/clients');
    }
    if (path === '/immobilier') {
      return location.pathname.startsWith('/immobilier');
    }
    if (path === '/immobilisations') {
      return location.pathname.startsWith('/immobilisations');
    }
    if (path === '/vie-scolaire') {
      return location.pathname.startsWith('/vie-scolaire');
    }
    if (path === '/caisse') {
      return location.pathname === '/caisse' ||
        location.pathname.startsWith('/caisse/') ||
        location.pathname === '/vente-directe';
    }
    if (path === '/stock') {
      return location.pathname === '/stock' ||
        location.pathname === '/inventaire';
    }
    if (path === '/admin/bridge') {
      return location.pathname.startsWith('/admin/bridge');
    }
    if (path === '/demandes-decaissement') {
      return location.pathname.startsWith('/demandes-decaissement') ||
        location.pathname.startsWith('/decaissements-directs') ||
        location.pathname.startsWith('/decaissements/');
    }
    return location.pathname === path ||
      (path === '/dashboard' && location.pathname === '/');
  };

  // Fonction pour basculer l'ouverture d'un sous-menu
  const toggleSubmenu = (menuId: string) => {
    setOpenSubmenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  // Vérifier si un sous-menu doit être ouvert (si un de ses enfants est actif)
  const shouldSubmenuBeOpen = (menuId: string, paths: string[]): boolean => {
    return paths.some(path => location.pathname.startsWith(path));
  };

  // Ouvrir le sous-menu contenant la route active.
  //
  // Le merge est NON DESTRUCTIF, comme pour les super-blocs plus bas : on
  // ouvre la section concernee, on ne referme jamais les autres. L'ancienne
  // version remplacait l'etat entier a chaque navigation, si bien qu'une
  // section restait ouverte uniquement si la nouvelle route figurait dans la
  // liste de chemins ci-dessous — une liste tenue a la main, que chaque
  // nouvelle entree de menu rendait un peu plus incomplete. Resultat : cliquer
  // sur « Evaluation & Performances » ou « Procedure disciplinaire » repliait
  // tout Mon espace, alors que l'utilisateur venait d'y entrer.
  useEffect(() => {
    setSearchQuery('');
    setOpenSubmenus(prev => ({
      ...prev,
      administration: prev.administration || shouldSubmenuBeOpen('administration', [
        '/administration',
        '/supervision',
        '/alertes'
      ]),
      rh: true,
      monEspace: prev.monEspace || shouldSubmenuBeOpen('monEspace', [
        '/mon-espace',
        '/rh/conges',
        '/rh/absences',
        '/rh/formations',
        '/rh/alertes',
        '/rh/medical',
        '/rh/demissions',
        '/mes-taches',
        '/tableau-taches',
        '/rh/disponibilite',
        '/rh/documents-entreprise',
        '/rh/plan-carriere',
        '/mon-espace/ma-fiche-de-poste',
        '/rh/evenements',
        '/rh/annonces',
        '/rh/team-building',
      ]),
      immobilisations: prev.immobilisations || shouldSubmenuBeOpen('immobilisations', [
        '/immobilisations',
        '/immobilisations/etats',
        '/immobilisations/inventaires',
        '/immobilisations/integrite',
      ]),
      societe: prev.societe || shouldSubmenuBeOpen('societe', [
        '/societe',
        '/dividendes',
      ]),
    }));
  }, [location.pathname]);

  // ── Auto-ouverture du super-bloc contenant la route active ────────────────
  // En plus de l'auto-ouverture du sous-menu (gérée ci-dessus), on déplie le
  // super-bloc parent de l'item actif afin que l'utilisateur le voie toujours.
  // On ne REPLIE jamais automatiquement les autres blocs : on respecte la
  // préférence localStorage de l'utilisateur (merge non destructif).
  useEffect(() => {
    const activeItem = filteredMenuItems.find(it => isMenuItemActive(it.path));
    if (!activeItem) return;
    const parentBloc = SUPER_BLOCS.find(b => b.itemIds.includes(activeItem.id));
    if (!parentBloc) return;
    setOpenBlocs(prev => (prev[parentBloc.id] ? prev : { ...prev, [parentBloc.id]: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, filteredMenuItems]);

  return (
    <>
      {/* Overlay pour mobile */}
      {sidebarOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar — drawer mobile / sidebar permanente desktop (MOB-4) */}
      {/* Le fond reprend la couleur de l'entité, en surface diluée. Le blanc
          reste le repli : une entité sans couleur définie garde l'apparence
          d'origine. */}
      <div
        data-barre-laterale
        className={`
        print:hidden
        fixed md:static inset-y-0 left-0 z-50
        shadow-lg transition-transform duration-300 ease-out md:transition-all
        flex flex-col
        ${sidebarOpen ? 'w-[340px] translate-x-0' : 'w-20 -translate-x-full md:translate-x-0'}
      `}
        style={{ backgroundColor: 'var(--color-primary-surface, #ffffff)' }}
      >
        {/* En-tête avec logo — pt safe-area iOS pour notch (drawer mobile plein écran) */}
        <div
          className="flex items-center justify-between p-4 border-b"
          style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}
        >
          <div className={`flex items-center space-x-3 transition-all min-w-0 ${!sidebarOpen ? 'justify-center w-full' : ''}`}>
            {/* Logo : entité si disponible, sinon SAGeCOM SVG */}
            <div className="relative flex-shrink-0">
              {user?.entiteLogo ? (
                <img
                  src={user.entiteLogo}
                  alt={user.entiteNom ?? 'Logo'}
                  style={{ width: sidebarOpen ? 40 : 32, height: sidebarOpen ? 40 : 32 }}
                  className="object-contain rounded transition-all duration-300"
                />
              ) : (
                <svg
                  width={sidebarOpen ? "40" : "32"}
                  height={sidebarOpen ? "40" : "32"}
                  viewBox="0 0 200 200"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="transition-all duration-300"
                >
                  <circle cx="100" cy="100" r="90" stroke="url(#sbgrad)" strokeWidth="10" fill="white" />
                  <path d="M60 80 L140 80 L120 120 L80 120 L60 80Z" fill="url(#sbgrad)" />
                  <text x="100" y="140" textAnchor="middle" fill="var(--color-primary)" fontSize="24" fontWeight="bold">SAG</text>
                  <defs>
                    <linearGradient id="sbgrad" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="var(--color-primary)" />
                      <stop offset="100%" stopColor="var(--color-accent)" />
                    </linearGradient>
                  </defs>
                </svg>
              )}

              {/* Badge collapsed */}
              {!sidebarOpen && !user?.entiteLogo && (
                <div className="absolute -bottom-1 -right-1 text-white text-[8px] font-bold px-1 py-0.5 rounded shadow-sm"
                  style={{ background: 'var(--color-primary)' }}>
                  eCOM
                </div>
              )}
            </div>

            {/* Nom : entité ou SAG-eCOM */}
            {sidebarOpen && (
              <div className="flex flex-col min-w-0">
                {user?.entiteNom ? (
                  <>
                    <h1 className="text-base font-bold text-gray-800 truncate max-w-[140px]" title={user.entiteNom}>
                      {user.entiteNom}
                    </h1>
                    <p className="text-xs [color:var(--color-sidebar-muted,#9ca3af)] -mt-0.5">{t('sidebar.poweredBy')}</p>
                  </>
                ) : (
                  <>
                    <h1 className="text-xl font-bold text-gray-800 whitespace-nowrap">
                      SAG<span style={{ color: 'var(--color-primary)' }}>-eCOM</span>
                    </h1>
                    <p className="text-xs [color:var(--color-sidebar-muted,#9ca3af)] -mt-1">{t('sidebar.businessSuite')}</p>
                  </>
                )}
              </div>
            )}
          </div>

          {sidebarOpen ? (
            <button
              onClick={() => setSidebarOpen(false)}
              className="md:hidden p-1 hover:bg-gray-100 rounded flex-shrink-0"
            >
              <X className="h-5 w-5 text-gray-600" />
            </button>
          ) : (
            <button
              onClick={() => setSidebarOpen(true)}
              className="md:hidden p-1 hover:bg-gray-100 rounded flex-shrink-0"
            >
              <Menu className="h-5 w-5 text-gray-600" />
            </button>
          )}
        </div>

        {/* Barre de recherche — visible uniquement en mode étendu */}
        {sidebarOpen && (
          <div className="px-3 py-2 border-b">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 [color:var(--color-sidebar-muted,#9ca3af)] pointer-events-none" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Rechercher un menu..."
                className="w-full pl-8 pr-7 py-1.5 text-xs rounded-md border border-gray-200 bg-gray-50 focus:outline-none focus:ring-1 focus:ring-blue-300 focus:border-blue-300 placeholder-gray-400"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 [color:var(--color-sidebar-muted,#9ca3af)] hover:text-gray-600"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 p-4 overflow-y-auto">
          {(() => {
            // ── Rendu encapsulé en 2 niveaux (super-blocs) ──────────────────
            // renderMenuItem garde EXACTEMENT le rendu d'item d'origine
            // (header + sous-menus codés par item.id). On l'appelle ensuite
            // soit à plat (épinglés), soit groupé dans un super-bloc.
            const renderMenuItem = (item: MenuItemDef) => {
            const isActive = isMenuItemActive(item.path);
            const hasSubmenu = ['admin', 'monEspace', 'rh', 'immobilisations', 'societe', 'reporting', 'enquetes_satisfaction'].includes(item.id);
            const isOpen = openSubmenus[item.id];

            return (
              <div key={item.id} className="mb-1">
                {/* Menu principal */}
                <div
                  onClick={() => {
                    if (hasSubmenu && sidebarOpen) {
                      toggleSubmenu(item.id);
                    } else if (!hasSubmenu) {
                      navigate(item.path);
                      fermerSiDrawer();
                    }
                  }}
                  className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-all group ${isActive && !hasSubmenu
                    ? 'shadow-sm'
                    : 'text-gray-700 hover:bg-gray-50 hover:[color:var(--color-sidebar-text,#111827)] hover:shadow-sm'
                    } ${!sidebarOpen ? 'justify-center' : ''}`}
                  style={isActive && !hasSubmenu ? {
                    // Bleu fixe, et non la couleur d'entite : sur une barre
                    // teintee aux couleurs de la marque, un actif de la meme
                    // famille chromatique cessait de se distinguer.
                    backgroundColor: '#2563EB',
                    color: '#ffffff',
                  } : {}}
                  title={!sidebarOpen ? item.label : ''}
                >
                  <div className="flex items-center space-x-3">
                    <item.icon
                      className="h-5 w-5 flex-shrink-0 transition-colors"
                      style={isActive && !hasSubmenu
                        ? { color: '#ffffff' }
                        : {}}
                    />

                    {sidebarOpen && (
                      <span className="font-medium text-sm leading-tight line-clamp-2">
                        {item.label}
                      </span>
                    )}
                  </div>

                  {sidebarOpen && (
                    <div className="flex items-center">
                      {item.badge && (
                        <span className="bg-gradient-to-r from-green-500 to-emerald-600 text-white text-xs px-2 py-0.5 rounded-full animate-pulse mr-2">
                          {item.badge}
                        </span>
                      )}
                      {hasSubmenu && (
                        <button className="p-1 hover:bg-gray-200 rounded-full">
                          {isOpen ? (
                            <ChevronDown className="w-4 h-4 [color:var(--color-sidebar-muted,#9ca3af)]" />
                          ) : (
                            <ChevronRight className="w-4 h-4 [color:var(--color-sidebar-muted,#9ca3af)]" />
                          )}
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* Sous-menu pour Administration */}
                {sidebarOpen && hasSubmenu && isOpen && item.id === 'admin' && (
                  <div className="ml-8 mt-1 mb-2 space-y-1 border-l-2 border-gray-200 pl-2">
                    <LienMenu
                      to="/administration"
                      onClick={() => {
                        fermerSiDrawer();
                      }}
                      className="flex items-center space-x-2 p-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <Settings className="h-4 w-4" />
                      <span>{t('sidebar.admin.administration')}</span>
                    </LienMenu>
                    {hasPermission('access_alertes') && (
                      <LienMenu
                        to="/alertes"
                        onClick={() => {
                          fermerSiDrawer();
                        }}
                        className="flex items-center space-x-2 p-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <Bell className="h-4 w-4" />
                        <span>{t('sidebar.admin.alertes')}</span>
                      </LienMenu>
                    )}
                    {/* Configuration des alertes & notifications */}
                    {hasPermission('configurer_alertes') && (
                      <LienMenu
                        to="/administration/alertes"
                        onClick={() => {
                          fermerSiDrawer();
                        }}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                          location.pathname === '/administration/alertes'
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <Bell className="h-4 w-4" />
                        <span>Configuration des alertes</span>
                      </LienMenu>
                    )}
                    {/* Référentiels — Coupures (billets/pièces) */}
                    {hasPermission('gerer_referentiels') && (
                      <LienMenu
                        to="/admin/coupures"
                        onClick={() => {
                          fermerSiDrawer();
                        }}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                          location.pathname === '/admin/coupures'
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <Banknote className="h-4 w-4" />
                        <span>Référentiels — Coupures</span>
                      </LienMenu>
                    )}
                    {/* Référentiels — Devises (ISO 4217) */}
                    {hasPermission('gerer_referentiels') && (
                      <LienMenu
                        to="/admin/referentiel-devises"
                        onClick={() => {
                          fermerSiDrawer();
                        }}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                          location.pathname === '/admin/referentiel-devises'
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <Coins className="h-4 w-4" />
                        <span>Référentiels — Devises</span>
                      </LienMenu>
                    )}
                    {/* Référentiels applicatifs (modules, journaux, sens…) */}
                    {hasPermission('gerer_referentiels') && (
                      <LienMenu
                        to="/admin/referentiels"
                        onClick={() => {
                          fermerSiDrawer();
                        }}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                          location.pathname === '/admin/referentiels'
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <Database className="h-4 w-4" />
                        <span>Référentiels applicatifs</span>
                      </LienMenu>
                    )}
                    {/* MM-117e — Barèmes des frais (DOSSIER / ÉTUDE / ENTRÉE)
                        avec décomposition obligatoire en lignes typées
                        (prestations / main d'œuvre / matériels). */}
                    {hasPermission('gerer_referentiels') && (
                      <LienMenu
                        to="/admin/baremes-frais-entree"
                        onClick={() => {
                          fermerSiDrawer();
                        }}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                          location.pathname.startsWith('/admin/baremes-frais-entree')
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <Calculator className="h-4 w-4" />
                        <span>Barèmes des frais</span>
                      </LienMenu>
                    )}
                    {/* WS-Variantes V2c — Conventions de subvention (branchements subventionnés) */}
                    {hasPermission('gerer_referentiels') && (
                      <LienMenu
                        to="/admin/conventions-subvention"
                        onClick={() => {
                          fermerSiDrawer();
                        }}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                          location.pathname.startsWith('/admin/conventions-subvention')
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <HandCoins className="h-4 w-4" />
                        <span>Conventions de subvention</span>
                      </LienMenu>
                    )}
                    {/* Regroupements (ex. regroupements de branchements électricité) */}
                    {hasPermission('gerer_regroupements') && (
                      <LienMenu
                        to="/parametrage/regroupements"
                        onClick={() => {
                          fermerSiDrawer();
                        }}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                          location.pathname.startsWith('/parametrage/regroupements')
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <Layers className="h-4 w-4" />
                        <span>Regroupements</span>
                      </LienMenu>
                    )}
                    {/* Référentiels fiscaux (types entreprise + taux IRCM/IS) */}
                    {hasPermission('gerer_referentiels') && (
                      <LienMenu
                        to="/administration/referentiels-fiscaux"
                        onClick={() => {
                          fermerSiDrawer();
                        }}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                          location.pathname === '/administration/referentiels-fiscaux'
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <Receipt className="h-4 w-4" />
                        <span>Référentiels fiscaux</span>
                      </LienMenu>
                    )}
                    {/* Axes analytiques dynamiques (mai 2026) */}
                    {hasPermission('gerer_referentiels') && (
                      <LienMenu
                        to="/administration/axes-analytiques"
                        onClick={() => {
                          fermerSiDrawer();
                        }}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                          location.pathname === '/administration/axes-analytiques'
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <Layers className="h-4 w-4" />
                        <span>Axes analytiques</span>
                      </LienMenu>
                    )}
                    {/* Modèles de documents PDF par entité */}
                    {hasPermission('gerer_templates') && (
                      <LienMenu
                        to="/admin/templates-documents"
                        onClick={() => {
                          fermerSiDrawer();
                        }}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                          location.pathname === '/admin/templates-documents'
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <FileText className="h-4 w-4" />
                        <span>Modèles de documents</span>
                      </LienMenu>
                    )}
                    {/* Module Développeur — config avancée types d'opération */}
                    {hasPermission('access_devmode') && (
                      <LienMenu
                        to="/admin/dev/types-operation"
                        onClick={() => {
                          fermerSiDrawer();
                        }}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                          location.pathname.startsWith('/admin/dev/types-operation')
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <Code className="h-4 w-4" />
                        <span>DevMode — Types d'opération</span>
                      </LienMenu>
                    )}
                    {/* Module Développeur — gestion des modules SAGeCom */}
                    {hasPermission('access_devmode') && (
                      <LienMenu
                        to="/admin/dev/modules"
                        onClick={() => {
                          fermerSiDrawer();
                        }}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                          location.pathname === '/admin/dev/modules'
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <Code className="h-4 w-4" />
                        <span>Configuration — Modules de la solution</span>
                      </LienMenu>
                    )}
                    {/* Paramétrage — Banques de l'entité */}
                    {hasPermission('gerer_referentiels') && (
                      <LienMenu
                        to="/parametrage/banques"
                        onClick={() => {
                          fermerSiDrawer();
                        }}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                          location.pathname === '/parametrage/banques'
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <Landmark className="h-4 w-4" />
                        <span>Banques de l'entité</span>
                      </LienMenu>
                    )}
                    {/* Paramétrage — Opérateurs Mobile Money */}
                    {hasPermission('gerer_referentiels') && (
                      <LienMenu
                        to="/parametrage/operateurs-mobile-money"
                        onClick={() => {
                          fermerSiDrawer();
                        }}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                          location.pathname === '/parametrage/operateurs-mobile-money'
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <Smartphone className="h-4 w-4" />
                        <span>Opérateurs Mobile Money</span>
                      </LienMenu>
                    )}
                    {/* Paramétrage — Barèmes des frais de mission.
                        Gardé par le droit de CONSULTATION : le gestionnaire de
                        paie doit pouvoir vérifier un taux sans pouvoir le modifier
                        (la modification est gardée dans la page par
                        `gerer_baremes_mission`). */}
                    {hasPermission('access_baremes_mission') && (
                      <LienMenu
                        to="/parametrage/baremes-mission"
                        onClick={() => {
                          fermerSiDrawer();
                        }}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                          location.pathname === '/parametrage/baremes-mission'
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <Coins className="h-4 w-4" />
                        <span>Barèmes frais de mission</span>
                      </LienMenu>
                    )}
                    {/* Paramétrage — Cours de devises.
                        Gardé par le droit de CONSULTATION : un comptable doit
                        pouvoir vérifier quel cours s'applique à une date sans
                        pouvoir le modifier (l'écriture est gardée dans la page
                        par `gerer_cours_devises`). */}
                    {hasPermission('access_cours_devises') && (
                      <LienMenu
                        to="/parametrage/cours-devises"
                        onClick={() => {
                          fermerSiDrawer();
                        }}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                          location.pathname === '/parametrage/cours-devises'
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <ArrowLeftRight className="h-4 w-4" />
                        <span>Cours de devises</span>
                      </LienMenu>
                    )}
                    {/* NB : « Tarifs électriques » et « Facturation électrique » ont été
                        déplacés dans le menu de premier niveau « Électricité » (EEC),
                        conditionné par les droits dédiés gerer_tarifs_electriques /
                        access_facturation_electrique. */}
                    {/* Paiements — Historique CinetPay */}
                    {hasPermission('voir_historique_paiements') && (
                      <LienMenu
                        to="/admin/cinetpay/historique"
                        onClick={() => {
                          fermerSiDrawer();
                        }}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                          location.pathname === '/admin/cinetpay/historique'
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <CreditCard className="h-4 w-4" />
                        <span>Paiements — Historique CinetPay</span>
                      </LienMenu>
                    )}
                    {/* Agrégateur de paiement — marchands, liens/QR, transactions */}
                    {hasPermission('access_agregateur_paiement') && (
                      <LienMenu
                        to="/agregateur"
                        onClick={() => {
                          fermerSiDrawer();
                        }}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                          location.pathname.startsWith('/agregateur')
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <Wallet className="h-4 w-4" />
                        <span>Agrégateur paiement</span>
                      </LienMenu>
                    )}
                    {/* Bridge (Interfaçage cross-app) déplacé hors du bloc Administration :
                        désormais rendu comme entrée propre du super-bloc « Interfaces & utilitaires »
                        (item menuItems id='bridge'). */}
                  </div>
                )}

                {/* Sous-menu RH — rendu via SidebarAutoModule (autoSidebar=true dans le catalogue) */}
                {false && item.id === '_rh_removed' && (
                  <div className="ml-8 mt-1 mb-2 space-y-1 border-l-2 border-gray-200 pl-2">
                    {/* Dashboard RH */}
                    <LienMenu
                      to="/rh/dashboard"
                      onClick={() => {
                        fermerSiDrawer();
                      }}
                      className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname === '/rh/dashboard'
                          ? 'text-blue-700 bg-blue-50 font-medium'
                          : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                        }`}
                    >
                      <LayoutDashboard className="h-4 w-4" />
                      <span>{t('sidebar.rh.tableauDeBordRH')}</span>
                    </LienMenu>
                    {/* Mai 2026 — Chantier JJ-1 : tableau de bord RH (vues TDB 2017) */}
                    {hasPermission('access_rh') && (
                      <LienMenu
                        to="/rh/tableau-de-bord"
                        onClick={fermerSiDrawer}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname === '/rh/tableau-de-bord'
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                      >
                        <BarChart3 className="h-4 w-4" />
                        <span>Tableau de bord RH</span>
                        <span className="ml-auto text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-full font-semibold">NEW</span>
                      </LienMenu>
                    )}

                    {/* Alertes RH */}
                    {hasPermission('access_rh') && (
                      <LienMenu
                        to="/rh/alertes"
                        onClick={fermerSiDrawer}
                        className={`flex items-center justify-between p-2 text-sm rounded-lg transition-colors ${location.pathname === '/rh/alertes'
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : alertesRHCount > 0 ? 'text-red-600 hover:bg-red-50' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                      >
                        <div className="flex items-center space-x-2">
                          <div className="relative">
                            <Bell className={`h-4 w-4 ${alertesRHCount > 0 ? 'animate-[swing_1s_ease-in-out_infinite]' : ''}`} />
                            {alertesRHCount > 0 && (
                              <span className="absolute -top-1.5 -right-1.5 w-2 h-2 bg-red-500 rounded-full animate-ping" />
                            )}
                          </div>
                          <span>Alertes RH</span>
                        </div>
                        {alertesRHCount > 0 && (
                          <span className="bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center animate-pulse">
                            {alertesRHCount}
                          </span>
                        )}
                      </LienMenu>
                    )}

                    {/* Demandes RH */}
                    {hasPermission('access_rh') && (
                      <LienMenu
                        to="/rh/demandes"
                        onClick={fermerSiDrawer}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/demandes')
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                      >
                        <Inbox className="h-4 w-4" />
                        <span>{t('sidebar.rh.demandesRH')}</span>
                      </LienMenu>
                    )}

                    {/* Organisation */}
                    {hasPermission('access_rh') && (
                      <LienMenu
                        to="/rh/organisation"
                        onClick={fermerSiDrawer}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/organisation')
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                      >
                        <Building2 className="h-4 w-4" />
                        <span>{t('sidebar.rh.organisation')}</span>
                      </LienMenu>
                    )}

                    {/* Conflits d'intérêts.
                        Gardé par le droit de DÉCLARER, le plus large : chaque
                        collaborateur doit voir cette entrée pour pouvoir déposer une
                        déclaration. Le référent y trouve en plus le registre complet —
                        c'est le backend qui fait la différence, pas l'affichage du menu. */}
                    {hasPermission('declarer_conflit_interet') && (
                      <LienMenu
                        to="/conflits-interet"
                        onClick={fermerSiDrawer}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/conflits-interet')
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                      >
                        <ShieldCheck className="h-4 w-4" />
                        <span>Conflits d'intérêts</span>
                      </LienMenu>
                    )}

                    {/* Employés */}
                    {hasPermission('access_employes') && (
                      <LienMenu
                        to="/rh/employes"
                        onClick={() => {
                          fermerSiDrawer();
                        }}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/employes')
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                      >
                        <Users className="h-4 w-4" />
                        <span>{t('sidebar.rh.employes')}</span>
                      </LienMenu>
                    )}

                    {/* Contrats */}
                    {hasPermission('access_contrats') && (
                      <LienMenu
                        to="/rh/contrats"
                        onClick={() => {
                          fermerSiDrawer();
                        }}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname === '/rh/contrats' || (location.pathname.startsWith('/rh/contrats/') && !location.pathname.startsWith('/rh/contrats/import'))
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                      >
                        <FileText className="h-4 w-4" />
                        <span>{t('sidebar.rh.contrats')}</span>
                      </LienMenu>
                    )}

                    {/* Importer un contrat depuis un PDF */}
                    {hasPermission('access_contrats') && (
                      <LienMenu
                        to="/rh/contrats/import"
                        onClick={() => {
                          fermerSiDrawer();
                        }}
                        className={`flex items-center space-x-2 p-2 pl-6 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/contrats/import')
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                      >
                        <Upload className="h-4 w-4" />
                        <span>Importer un contrat (PDF)</span>
                      </LienMenu>
                    )}

                    {/* Paie */}
                    {hasPermission('access_paie') && (
                      <LienMenu
                        to="/rh/paie"
                        onClick={() => {
                          fermerSiDrawer();
                        }}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/paie')
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                      >
                        <DollarSign className="h-4 w-4" />
                        <span>{t('sidebar.rh.paie')}</span>
                      </LienMenu>
                    )}

                    {/* Rémunération mandataires sociaux (gérants non salariés) */}
                    {hasPermission('view_remuneration_mandataires') && (
                      <LienMenu
                        to="/rh/remuneration-mandataires"
                        onClick={() => {
                          fermerSiDrawer();
                        }}
                        title="Rémunération des gérants non salariés (SARL U) — IRG + CN, sans CNPS"
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/remuneration-mandataires')
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                      >
                        <Crown className="h-4 w-4" />
                        <span>Rémunération mandataires</span>
                      </LienMenu>
                    )}

                    {/* Délégations & Intérims */}
                    {hasPermission('access_rh') && (
                      <LienMenu
                        to="/rh/delegations"
                        onClick={fermerSiDrawer}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/delegations')
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                      >
                        <ArrowRightLeft className="h-4 w-4" />
                        <span>Délégations &amp; Intérims</span>
                      </LienMenu>
                    )}

                    {/* Demandes de congés */}
                    {hasPermission('access_conges') && (
                      <LienMenu
                        to="/rh/conges"
                        onClick={() => {
                          fermerSiDrawer();
                        }}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/conges')
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                      >
                        <Calendar className="h-4 w-4" />
                        <span>{t('sidebar.rh.demandesConges')}</span>
                      </LienMenu>
                    )}

                    {/* Demandes d'absence */}
                    {hasPermission('access_conges') && (
                      <LienMenu
                        to="/rh/absences"
                        onClick={() => {
                          fermerSiDrawer();
                        }}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/absences')
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                      >
                        <UserX className="h-4 w-4" />
                        <span>{t('sidebar.rh.demandesAbsence')}</span>
                      </LienMenu>
                    )}

                    {/* Recrutement */}
                    {hasPermission('access_recrutement') && (
                      <LienMenu
                        to="/rh/recrutement"
                        onClick={() => {
                          fermerSiDrawer();
                        }}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/recrutement')
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                      >
                        <Briefcase className="h-4 w-4" />
                        <span>{t('sidebar.rh.recrutement')}</span>
                      </LienMenu>
                    )}

                    {/* Évaluations */}
                    {hasPermission('access_rh') && (
<LienMenu
                        to="/rh/evaluations"
                        onClick={() => {
                          fermerSiDrawer();
                        }}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/evaluations')
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                      >
                        <CheckCircle className="h-4 w-4" />
                        <span>{t('sidebar.rh.evaluations')}</span>
                      </LienMenu>
                    )}

                    {/* Catalogue formations (gestionnaire) */}
                    {hasPermission('access_rh') && (
                      <LienMenu
                        to="/rh/formations"
                        onClick={() => {
                          fermerSiDrawer();
                        }}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/formations')
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                      >
                        <GraduationCap className="h-4 w-4" />
                        <span>{t('sidebar.rh.formations')}</span>
                      </LienMenu>
                    )}

                    {/* États & Rapports RH */}
                    {hasPermission('access_rh') && (
                      <LienMenu
                        to="/rh/etats"
                        onClick={() => {
                          fermerSiDrawer();
                        }}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/etats')
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                      >
                        <FileBarChart className="h-4 w-4" />
                        <span>États &amp; Rapports</span>
                      </LienMenu>
                    )}

                    {/* Campagnes de primes — mai 2026 */}
                    {hasPermission('access_paie') && (
                      <LienMenu
                        to="/rh/campagnes-primes"
                        onClick={() => {
                          fermerSiDrawer();
                        }}
                        title="Campagnes collectives ou ciblées (résultats, objectifs, exceptionnelles)"
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/campagnes-primes')
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                      >
                        <Gift className="h-4 w-4" />
                        <span>Campagnes de primes</span>
                      </LienMenu>
                    )}

                    {/* Évaluation / Performance — Lot 3 */}
                    {hasPermission('access_evaluation_performance') && (
                      <LienMenu
                        to="/evaluation-performance"
                        onClick={() => {
                          fermerSiDrawer();
                        }}
                        title="Objectifs, auto-évaluation, évaluation manager et pilotage RH des campagnes"
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/evaluation-performance')
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                      >
                        <Target className="h-4 w-4" />
                        <span>Évaluation &amp; Performance</span>
                      </LienMenu>
                    )}

                    <div className="border-t border-gray-200 my-1"></div>
                    <p className="text-[10px] font-semibold [color:var(--color-sidebar-muted,#9ca3af)] uppercase tracking-wide px-2 pt-1">{t('sidebar.rh.gestionRH')}</p>

                    {hasPermission('access_rh') && (
                      <LienMenu
                        to="/rh/disponibilite"
                        onClick={fermerSiDrawer}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/disponibilite')
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                      >
                        <Briefcase className="h-4 w-4" />
                        <span>{t('sidebar.rh.miseADisponibilite')}</span>
                      </LienMenu>
                    )}

                    {hasPermission('access_employes') && (
                      <LienMenu
                        to="/rh/demissions"
                        onClick={fermerSiDrawer}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/demissions')
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                      >
                        <LogOut className="h-4 w-4" />
                        <span>{t('sidebar.rh.demissions')}</span>
                      </LienMenu>
                    )}

                    {hasPermission('access_employes') && (
                      <LienMenu
                        to="/rh/disciplinaire"
                        onClick={fermerSiDrawer}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/disciplinaire')
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                      >
                        <Shield className="h-4 w-4" />
                        <span>{t('sidebar.rh.procedureDisciplinaire')}</span>
                      </LienMenu>
                    )}

                    {hasPermission('access_rh') && (
                      <LienMenu
                        to="/rh/pointage"
                        onClick={fermerSiDrawer}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/pointage')
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                      >
                        <Clock className="h-4 w-4" />
                        <span>Pointage</span>
                      </LienMenu>
                    )}

                    {/* Mai 2026 — Paie journaliers (bordereaux hebdo) */}
                    {hasPermission('access_paie') && (
                      <LienMenu
                        to="/rh/bordereaux-journaliers"
                        onClick={fermerSiDrawer}
                        title="Paie hebdomadaire des journaliers (cash) — agrégation depuis pointages chantier"
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/bordereaux-journaliers')
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                      >
                        <Users className="h-4 w-4" />
                        <span>Paie journaliers</span>
                      </LienMenu>
                    )}

                    {/* Mai 2026 — Bordereaux de stages (paiement d'allocations + attestations DOC3) */}
                    {hasPermission('access_paie') && (
                      <LienMenu
                        to="/rh/bordereaux-stages"
                        onClick={fermerSiDrawer}
                        title="Paiement d'allocations de stage — workflow brouillon → validé → payé en caisse + attestations PDF automatiques"
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/bordereaux-stages')
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                      >
                        <GraduationCap className="h-4 w-4" />
                        <span>Paie stages</span>
                      </LienMenu>
                    )}

                    {/* Mai 2026 — Demandes de documents RH (certificat, attestation, CNPS, duplicata) */}
                    {hasPermission('access_rh') && (
                      <LienMenu
                        to="/rh/demandes-documents"
                        onClick={fermerSiDrawer}
                        title="Demandes de documents : certificat de travail, attestation d'emploi, déclaration CNPS, duplicata bulletin de paie"
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/demandes-documents')
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                      >
                        <FileText className="h-4 w-4" />
                        <span>Demandes documents</span>
                      </LienMenu>
                    )}

                    <div className="border-t border-gray-200 my-1"></div>
                    <p className="text-[10px] font-semibold [color:var(--color-sidebar-muted,#9ca3af)] uppercase tracking-wide px-2 pt-1">{t('sidebar.rh.relationsSociales')}</p>

                    {hasPermission('access_contrats') && (
                      <LienMenu
                        to="/rh/organismes-sociaux"
                        onClick={fermerSiDrawer}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/organismes-sociaux')
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                      >
                        <Building2 className="h-4 w-4" />
                        <span>{t('sidebar.rh.organismesSociaux')}</span>
                      </LienMenu>
                    )}

                    {hasPermission('access_rh') && (
                      <LienMenu
                        to="/rh/team-building"
                        onClick={fermerSiDrawer}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/team-building')
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                      >
                        <Users className="h-4 w-4" />
                        <span>{t('sidebar.rh.teamBuilding')}</span>
                      </LienMenu>
                    )}

                    {/* Médecine du travail */}
                    <div className="pt-1 pb-0.5">
                      <p className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-wide [color:var(--color-sidebar-muted,#9ca3af)]">
                        Médecine du travail
                      </p>
                    </div>

                    <LienMenu
                      to="/rh/medical/mon-dossier"
                      onClick={fermerSiDrawer}
                      className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname === '/rh/medical/mon-dossier'
                          ? 'text-blue-700 bg-blue-50 font-medium'
                          : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                        }`}
                    >
                      <User className="h-4 w-4" />
                      <span>Mon dossier médical</span>
                    </LienMenu>

                    {hasPermission('access_dossier_medical') && (
                      <LienMenu
                        to="/rh/medical"
                        onClick={fermerSiDrawer}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname === '/rh/medical' || location.pathname.startsWith('/rh/medical/dossier')
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                      >
                        <Stethoscope className="h-4 w-4" />
                        <span>Dossiers médicaux</span>
                      </LienMenu>
                    )}

                    {(hasPermission('access_dossier_medical') || hasPermission('voir_arrets_maladie')) && (
                      <LienMenu
                        to="/rh/medical/arrets"
                        onClick={fermerSiDrawer}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname === '/rh/medical/arrets'
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                      >
                        <CalendarOff className="h-4 w-4" />
                        <span>Arrêts maladie</span>
                      </LienMenu>
                    )}

                    {/* Parc automobile : véhicules, entretien, carburant, sinistres,
                        coûts. Un agent qui n'a que le droit de réserver y arrive
                        directement sur le pool. */}
                    {(hasPermission('access_parc_auto') || hasPermission('gerer_parc_auto')
                      || hasPermission('saisir_plein_carburant') || hasPermission('revoir_anomalie_carburant')) && (
                      <LienMenu
                        to="/parc-auto"
                        onClick={fermerSiDrawer}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/parc-auto')
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                      >
                        <Car className="h-4 w-4" />
                        <span>Parc automobile</span>
                      </LienMenu>
                    )}
                    {hasPermission('reserver_vehicule_pool') && !hasPermission('access_parc_auto') && !hasPermission('gerer_parc_auto') && (
                      <LienMenu
                        to="/parc-auto?onglet=pool"
                        onClick={fermerSiDrawer}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/parc-auto')
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                      >
                        <Car className="h-4 w-4" />
                        <span>Réserver un véhicule</span>
                      </LienMenu>
                    )}

                    {/* Sante et securite au travail : accidents, visites d'aptitude,
                        document unique. Complete la medecine du travail curative
                        ci-dessus par le volet reglementaire. */}
                    {(hasPermission('declarer_accident_travail') || hasPermission('gerer_sst')
                      || hasPermission('acces_medecine_travail')) && (
                      <LienMenu
                        to="/rh/sante-securite"
                        onClick={fermerSiDrawer}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/sante-securite')
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                      >
                        <HeartPulse className="h-4 w-4" />
                        <span>Santé & sécurité</span>
                      </LienMenu>
                    )}

                    {/* Types d'opération dynamiques (DevMode) ciblant rh */}
                    <SidebarDynamicTypes
                      moduleCode="rh"
                      closeMobile={() => setSidebarOpen(false)} />
                  </div>
                )}

                {/* Sous-menu Reporting & BI */}
                {sidebarOpen && hasSubmenu && isOpen && item.id === 'reporting' && (
                  <div className="ml-8 mt-1 mb-2 space-y-1 border-l-2 border-gray-200 pl-2">
<LienMenu
                      to="/statistiques"
                      onClick={fermerSiDrawer}
                      className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname === '/statistiques' ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                    >
                      <BarChart3 className="h-4 w-4" />
                      <span>{t('sidebar.reporting.statistiques')}</span>
                    </LienMenu>
                    {hasPermission('voir_bilan_periodique') && (
<LienMenu
                        to="/bilan-periodique"
                        onClick={fermerSiDrawer}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname === '/bilan-periodique' ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                      >
                        <PieChart className="h-4 w-4" />
                        <span>{t('sidebar.reporting.bilanPeriodique')}</span>
                      </LienMenu>
                    )}

                    {/* Index transversal du reporting : liens vers les hubs États
                        existants de chaque module. Chaque entrée est gardée par la MÊME
                        permission que sa route cible (cf. requiredPermission dans
                        AppRoutes.tsx) pour que la visibilité corresponde à l'accès. */}
                    <p className="px-2 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide [color:var(--color-sidebar-muted,#9ca3af)]">
                      États par domaine
                    </p>
                    {ETATS_PAR_DOMAINE.map((e) => {
                      if (!hasPermission(e.permission)) return null;
                      // Comparaison sur le pathname uniquement (les routes avec
                      // query ?tab=etats restent actives sur leur page cible).
                      const base = e.path.split('?')[0];
                      const isActive = e.matchPrefix
                        ? location.pathname.startsWith(base)
                        : location.pathname === base;
                      const Icon = e.icon;
                      return (
                        <LienMenu
                          key={e.path}
                          to={e.path}
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${isActive ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                        >
                          <Icon className="h-4 w-4" />
                          <span>{e.label}</span>
                        </LienMenu>
                      );
                    })}
                  </div>
                )}

                {/* Sous-menu Immobilisations (Liste + États SYSCOHADA) */}
                {sidebarOpen && hasSubmenu && isOpen && item.id === 'immobilisations' && (
                  <div className="ml-8 mt-1 mb-2 space-y-1 border-l-2 border-gray-200 pl-2">
                    <LienMenu
                      to="/immobilisations"
                      onClick={fermerSiDrawer}
                      className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                        location.pathname === '/immobilisations'
                          ? 'text-blue-700 bg-blue-50 font-medium'
                          : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                      }`}
                    >
                      <Boxes className="h-4 w-4" />
                      <span>Liste & catégories</span>
                    </LienMenu>
                    {hasPermission('access_immobilisations') && (
                      <LienMenu
                        to="/immobilisations/etats"
                        onClick={fermerSiDrawer}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                          location.pathname === '/immobilisations/etats'
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                        }`}
                      >
                        <FileBarChart className="h-4 w-4" />
                        <span>États SYSCOHADA</span>
                      </LienMenu>
                    )}
                    {hasPermission('access_immobilisations') && (
                      <LienMenu
                        to="/immobilisations/inventaires"
                        onClick={fermerSiDrawer}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                          location.pathname.startsWith('/immobilisations/inventaires')
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                        }`}
                      >
                        <ScanLine className="h-4 w-4" />
                        <span>Inventaires physiques</span>
                      </LienMenu>
                    )}
                    {hasPermission('access_immobilisations') && (
                      <LienMenu
                        to="/immobilisations/integrite"
                        onClick={fermerSiDrawer}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                          location.pathname === '/immobilisations/integrite'
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                        }`}
                      >
                        <ShieldCheck className="h-4 w-4" />
                        <span>Contrôles d'intégrité</span>
                      </LienMenu>
                    )}
                    {hasPermission('access_immobilisations') && (
                      <LienMenu
                        to="/immobilisations/subventions"
                        onClick={fermerSiDrawer}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                          location.pathname === '/immobilisations/subventions'
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                        }`}
                      >
                        <HandCoins className="h-4 w-4" />
                        <span>Subventions</span>
                      </LienMenu>
                    )}
                    {hasPermission('access_immobilisations') && (
                      <LienMenu
                        to="/immobilisations/credit-bail"
                        onClick={fermerSiDrawer}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                          location.pathname.startsWith('/immobilisations/credit-bail')
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                        }`}
                      >
                        <FileText className="h-4 w-4" />
                        <span>Crédit-bail</span>
                      </LienMenu>
                    )}
                    {hasPermission('access_immobilisations') && (
                      <LienMenu
                        to="/immobilisations/maintenance"
                        onClick={fermerSiDrawer}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                          location.pathname.startsWith('/immobilisations/maintenance')
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                        }`}
                      >
                        <Wrench className="h-4 w-4" />
                        <span>Maintenance</span>
                      </LienMenu>
                    )}
                    {hasPermission('access_immobilisations') && (
                      <LienMenu
                        to="/immobilisations/garanties"
                        onClick={fermerSiDrawer}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                          location.pathname.startsWith('/immobilisations/garanties')
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                        }`}
                      >
                        <Shield className="h-4 w-4" />
                        <span>Garanties</span>
                      </LienMenu>
                    )}
                    {hasPermission('access_immobilisations') && (
                      <LienMenu
                        to="/immobilisations/cartographie"
                        onClick={fermerSiDrawer}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                          location.pathname === '/immobilisations/cartographie'
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                        }`}
                      >
                        <MapIcon className="h-4 w-4" />
                        <span>Cartographie GPS</span>
                      </LienMenu>
                    )}
                    {hasPermission('cloturer_exercice_immobilisations') && (
                      <LienMenu
                        to="/immobilisations/cloture"
                        onClick={fermerSiDrawer}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                          location.pathname === '/immobilisations/cloture'
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                        }`}
                      >
                        <Lock className="h-4 w-4" />
                        <span>Clôture exercice</span>
                      </LienMenu>
                    )}
                  </div>
                )}

                {/* Sous-menu Mon espace (self-service employe) */}
                {sidebarOpen && hasSubmenu && isOpen && item.id === 'monEspace' && (
                  <div className="ml-8 mt-1 mb-2 space-y-1 border-l-2 border-gray-200 pl-2">

                    {/* ══ TABLEAUX DE BORDS ════════════════════════════ */}
                    <button
                      onClick={() => toggleMeEspace('tableaux')}
                      className="w-full flex items-center justify-between px-2 py-1.5 mt-2 rounded-md hover:bg-gray-100 transition-colors group"
                    >
                      <span className="text-[11px] font-bold [color:var(--color-sidebar-text,#111827)] uppercase tracking-wider">Tableaux de Bords</span>
                      <ChevronDown className={`h-3 w-3 [color:var(--color-sidebar-muted,#9ca3af)] transition-transform ${meEspaceOpen.tableaux ? 'rotate-0' : '-rotate-90'}`} />
                    </button>
                    {meEspaceOpen.tableaux && (<div className="ml-2 space-y-0.5">

                    {/* Tableau de bord Mon espace */}
                    <LienMenu
                      to="/mon-espace"
                      onClick={fermerSiDrawer}
                      className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                        location.pathname === '/mon-espace'
                          ? 'text-blue-700 bg-blue-50 font-medium'
                          : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                      }`}
                    >
                      <LayoutDashboard className="h-4 w-4" />
                      <span>{t('sidebar.monEspace.tableauDeBord')}</span>
                    </LienMenu>

                    {/* Mes tâches workflow avec badge live total/en-retard */}
                    <LienMenu
                      to="/mes-taches"
                      onClick={fermerSiDrawer}
                      title="Demandes en attente de votre action selon votre profil"
                      className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                        location.pathname.startsWith('/mes-taches')
                          ? 'text-blue-700 bg-blue-50 font-medium'
                          : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                      }`}
                    >
                      <ClipboardCheck className="h-4 w-4" />
                      <span className="flex-1">Mes tâches</span>
                      {mesTachesCount.total > 0 && (
                        <span className="flex items-center gap-1">
                          {mesTachesCount.enRetard > 0 && (
                            <span
                              className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center"
                              title={`${mesTachesCount.enRetard} tâche(s) en retard`}
                            >
                              {mesTachesCount.enRetard}
                            </span>
                          )}
                          <span className="bg-indigo-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                            {mesTachesCount.total}
                          </span>
                        </span>
                      )}
                    </LienMenu>

                    {/* Tableau des Taches */}
                    <LienMenu
                      to="/tableau-taches"
                      onClick={fermerSiDrawer}
                      className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                        location.pathname === '/tableau-taches'
                          ? 'text-blue-700 bg-blue-50 font-medium'
                          : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                      }`}
                    >
                      <ClipboardList className="h-4 w-4" />
                      <span>Tableau des Taches</span>
                    </LienMenu>

                    {/* Mes alertes */}
                    {hasModule('rh') && (
                      <LienMenu
                        to="/rh/alertes"
                        onClick={fermerSiDrawer}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                          location.pathname.startsWith('/rh/alertes')
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                        }`}
                      >
                        <Bell className="h-4 w-4" />
                        <span>Mes alertes</span>
                      </LienMenu>
                    )}

                    </div>)}

                    {/* ══ CARRIÈRE ════════════════════════════════════ */}
                    {hasModule('rh') && (
                      <>
                        <button
                          onClick={() => toggleMeEspace('carriere')}
                          className="w-full flex items-center justify-between px-2 py-1.5 mt-2 rounded-md hover:bg-gray-100 transition-colors group"
                        >
                          <span className="text-[11px] font-bold [color:var(--color-sidebar-text,#111827)] uppercase tracking-wider">Carrière</span>
                          <ChevronDown className={`h-3 w-3 [color:var(--color-sidebar-muted,#9ca3af)] transition-transform ${meEspaceOpen.carriere ? 'rotate-0' : '-rotate-90'}`} />
                        </button>
                        {meEspaceOpen.carriere && (<div className="ml-2 space-y-0.5">

                        {/* Ma carrière — entretiens, objectifs, auto-évaluation,
                            évaluation du responsable et bilan de campagne. */}
                        <LienMenu
                          to="/mon-espace/ma-carriere"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                            location.pathname === '/mon-espace/ma-carriere'
                              ? 'text-blue-700 bg-blue-50 font-medium'
                              : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                        >
                          <TrendingUp className="h-4 w-4" />
                          <span>Ma carrière</span>
                        </LienMenu>

                        {/* Ma fiche & famille */}
                        <LienMenu
                          to="/mon-espace/ma-fiche"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                            location.pathname === '/mon-espace/ma-fiche' || location.pathname === '/mon-espace/ma-fiche/modifier'
                              ? 'text-blue-700 bg-blue-50 font-medium'
                              : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                        >
                          <User className="h-4 w-4" />
                          <span>Ma fiche &amp; famille</span>
                        </LienMenu>

                        {/* Ma fiche de poste */}
                        <LienMenu
                          to="/mon-espace/ma-fiche-de-poste"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                            location.pathname === '/mon-espace/ma-fiche-de-poste'
                              ? 'text-blue-700 bg-blue-50 font-medium'
                              : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                        >
                          <Briefcase className="h-4 w-4" />
                          <span>Ma fiche de poste</span>
                        </LienMenu>

                        {/* Mon CV */}
                        <LienMenu
                          to="/mon-espace/mon-cv"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                            location.pathname === '/mon-espace/mon-cv'
                              ? 'text-blue-700 bg-blue-50 font-medium'
                              : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                        >
                          <FileText className="h-4 w-4" />
                          <span>Mon CV</span>
                        </LienMenu>

                        {/* Mes évaluations (vue employé) */}
                        <LienMenu
                          to="/mon-espace/evaluations"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                            location.pathname.startsWith('/mon-espace/evaluations')
                              ? 'text-blue-700 bg-blue-50 font-medium'
                              : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                        >
                          <Target className="h-4 w-4" />
                          <span>Mes évaluations</span>
                        </LienMenu>

                        {/* Mes enquêtes (self-service : répondre + historique) */}
                        <LienMenu
                          to="/mon-espace/mes-enquetes"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                            location.pathname.startsWith('/mon-espace/mes-enquetes')
                              ? 'text-blue-700 bg-blue-50 font-medium'
                              : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                        >
                          <ClipboardList className="h-4 w-4" />
                          <span>Mes enquêtes</span>
                        </LienMenu>

                        {/* Évaluation & Performances (module RH complet) */}
                        {hasPermission('access_evaluation_performance') && (
                          <LienMenu
                            to="/evaluation-performance"
                            onClick={fermerSiDrawer}
                            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                              location.pathname.startsWith('/evaluation-performance')
                                ? 'text-blue-700 bg-blue-50 font-medium'
                                : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                            }`}
                          >
                            <CheckCircle className="h-4 w-4" />
                            <span>Évaluation &amp; Performances</span>
                          </LienMenu>
                        )}

                        {/* Les pièces que l'agent doit fournir. C'est ici que mène la
                            relance qu'il reçoit — sans ce lien, le message lui dirait
                            quoi faire sans dire où. Aucune garde par droit : consulter
                            son dossier n'en demande aucun. */}
                        <LienMenu
                          to="/mon-espace/mon-dossier"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/mon-espace/mon-dossier') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                        >
                          <FolderCheck className="h-4 w-4" />
                          <span>Mon dossier</span>
                        </LienMenu>

                        {/* Mon propre deroule de carriere. La GESTION des campagnes
                            vit dans Capital Humain : elle donne acces aux salaires
                            proposes de tous, ce qui n'a pas sa place dans un espace
                            personnel. Sans garde par droit — consulter sa situation
                            n'en demande aucun, et le serveur filtre sur la fiche
                            employe du compte. */}
                        <LienMenu
                          to="/mon-espace/mes-avancements"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/mon-espace/mes-avancements')
                                ? 'text-blue-700 bg-blue-50 font-medium'
                                : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                              }`}
                        >
                          <TrendingUp className="h-4 w-4" />
                          <span>Mes avancements</span>
                        </LienMenu>

                        {/* Formations */}
                        <LienMenu
                          to="/rh/formations"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                            location.pathname.startsWith('/rh/formations')
                              ? 'text-blue-700 bg-blue-50 font-medium'
                              : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                        >
                          <GraduationCap className="h-4 w-4" />
                          <span>Formations</span>
                        </LienMenu>

                        {/* Mes distinctions — recues et proposees me concernant. */}
                        <LienMenu
                          to="/mon-espace/mes-distinctions"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/mon-espace/mes-distinctions')
                                ? 'text-blue-700 bg-blue-50 font-medium'
                                : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                              }`}
                        >
                          <Award className="h-4 w-4" />
                          <span>Mes distinctions</span>
                        </LienMenu>

                        {/* Procédure disciplinaire */}
                        {hasPermission('access_employes') && (
                          <LienMenu
                            to="/rh/disciplinaire"
                            onClick={fermerSiDrawer}
                            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                              location.pathname.startsWith('/rh/disciplinaire')
                                ? 'text-blue-700 bg-blue-50 font-medium'
                                : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                            }`}
                          >
                            <Shield className="h-4 w-4" />
                            <span>Procédure disciplinaire</span>
                          </LienMenu>
                        )}
                        </div>)}
                      </>
                    )}

                    {/* ══ DEMANDES ════════════════════════════════════ */}
                    {hasModule('rh') && (
                      <>
                        <button
                          onClick={() => toggleMeEspace('demandes')}
                          className="w-full flex items-center justify-between px-2 py-1.5 mt-2 rounded-md hover:bg-gray-100 transition-colors group"
                        >
                          <span className="text-[11px] font-bold [color:var(--color-sidebar-text,#111827)] uppercase tracking-wider">Demandes</span>
                          <ChevronDown className={`h-3 w-3 [color:var(--color-sidebar-muted,#9ca3af)] transition-transform ${meEspaceOpen.demandes ? 'rotate-0' : '-rotate-90'}`} />
                        </button>
                        {meEspaceOpen.demandes && (<div className="ml-2 space-y-0.5">

                        {/* Conflits d'intérêts.
                            La déclaration est un acte de l'agent lui-même : sa
                            place est dans le self-service, à côté des congés et
                            des absences. L'entrée du bloc RH, elle, sert au
                            référent déontologie qui instruit le registre. */}
                        {hasPermission('declarer_conflit_interet') && (
                          <LienMenu
                            to="/conflits-interet"
                            onClick={fermerSiDrawer}
                            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                              location.pathname.startsWith('/conflits-interet')
                                ? 'text-blue-700 bg-blue-50 font-medium'
                                : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                            }`}
                          >
                            <ShieldCheck className="h-4 w-4" />
                            <span>Conflits d'intérêts</span>
                          </LienMenu>
                        )}

                        {/* Congés */}
                        <LienMenu
                          to="/rh/conges"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                            location.pathname.startsWith('/rh/conges')
                              ? 'text-blue-700 bg-blue-50 font-medium'
                              : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                        >
                          <Calendar className="h-4 w-4" />
                          <span>Congés</span>
                        </LienMenu>

                        {/* Absence */}
                        <LienMenu
                          to="/rh/absences"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                            location.pathname.startsWith('/rh/absences')
                              ? 'text-blue-700 bg-blue-50 font-medium'
                              : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                        >
                          <UserX className="h-4 w-4" />
                          <span>Absence</span>
                        </LienMenu>

                        {/* Sortie de véhicule */}
                        <LienMenu
                          to="/demandes/nouvelle/RH_SORTIE_VEHICULE"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                            location.pathname.includes('RH_SORTIE_VEHICULE')
                              ? 'text-blue-700 bg-blue-50 font-medium'
                              : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                        >
                          <Car className="h-4 w-4" />
                          <span>Sortie de véhicule</span>
                        </LienMenu>

                        {/* Demande de formation */}
                        <LienMenu
                          to="/rh/formations"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                            location.pathname.startsWith('/rh/formations')
                              ? 'text-blue-700 bg-blue-50 font-medium'
                              : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                        >
                          <GraduationCap className="h-4 w-4" />
                          <span>Demande de formation</span>
                        </LienMenu>

                        {/* Documents / Disponibilité / Démission — chacun a
                            désormais son droit dédié (catégorie « RH —
                            Demandes du collaborateur »), ce qui permet de les
                            ouvrir profil par profil. Le lien suit le droit de
                            la route, sinon il mènerait à « Accès non
                            autorisé ». */}
                        {(hasPermission('access_demandes_documents') || hasPermission('access_rh')) && (
                          <LienMenu
                            to="/rh/demandes-documents"
                            onClick={fermerSiDrawer}
                            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                              location.pathname.startsWith('/rh/demandes-documents')
                                ? 'text-blue-700 bg-blue-50 font-medium'
                                : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                            }`}
                          >
                            <FileText className="h-4 w-4" />
                            <span>Documents</span>
                          </LienMenu>
                        )}

                        {(hasPermission('access_disponibilite') || hasPermission('access_rh')) && (
                          <LienMenu
                            to="/rh/disponibilite"
                            onClick={fermerSiDrawer}
                            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                              location.pathname.startsWith('/rh/disponibilite')
                                ? 'text-blue-700 bg-blue-50 font-medium'
                                : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                            }`}
                          >
                            <Briefcase className="h-4 w-4" />
                            <span>Disponibilité</span>
                          </LienMenu>
                        )}

                        {(hasPermission('access_demission') || hasPermission('access_rh')) && (
                          <LienMenu
                            to="/rh/demissions"
                            onClick={fermerSiDrawer}
                            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                              location.pathname.startsWith('/rh/demissions')
                                ? 'text-blue-700 bg-blue-50 font-medium'
                                : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                            }`}
                          >
                            <LogOut className="h-4 w-4" />
                            <span>Démission</span>
                          </LienMenu>
                        )}

                        {/* Avance sur salaire et prêt : ce sont des demandes
                            du collaborateur, au même titre qu'un congé. Le
                            suivi de leurs échéanciers reste dans Rémunération. */}
                        <LienMenu
                          to="/mon-espace/acompte"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                            location.pathname === '/mon-espace/acompte'
                              ? 'text-blue-700 bg-blue-50 font-medium'
                              : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                        >
                          <Wallet className="h-4 w-4" />
                          <span>Demande d'acompte</span>
                        </LienMenu>

                        <LienMenu
                          to="/mon-espace/pret"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                            location.pathname === '/mon-espace/pret'
                              ? 'text-blue-700 bg-blue-50 font-medium'
                              : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                        >
                          <Landmark className="h-4 w-4" />
                          <span>Demande de prêt</span>
                        </LienMenu>

                        {/* Mes demandes */}
                        <LienMenu
                          to="/mon-espace/mes-demandes"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                            location.pathname === '/mon-espace/mes-demandes'
                              ? 'text-blue-700 bg-blue-50 font-medium'
                              : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                        >
                          <FileText className="h-4 w-4" />
                          <span>{t('sidebar.monEspace.mesDemandes')}</span>
                        </LienMenu>

                        {/* Mes bulletins */}
                        <LienMenu
                          to="/mon-espace/bulletins"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                            location.pathname === '/mon-espace/bulletins'
                              ? 'text-blue-700 bg-blue-50 font-medium'
                              : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                        >
                          <Download className="h-4 w-4" />
                          <span>{t('sidebar.monEspace.mesBulletins')}</span>
                        </LienMenu>
                        </div>)}
                      </>
                    )}

                    {/* ══ PILOTAGE ════════════════════════════════════
                        Ouvert aussi à qui suit des projets : un manager sans
                        droit RH ne trouvait sinon jamais « Mes projets ». */}
                    {((hasModule('rh') && hasPermission('access_rh'))
                      || (hasModule('projets') && hasPermission('saisir_mes_projets'))) && (
                      <>
                        <button
                          onClick={() => toggleMeEspace('pilotage')}
                          className="w-full flex items-center justify-between px-2 py-1.5 mt-2 rounded-md hover:bg-gray-100 transition-colors group"
                        >
                          <span className="text-[11px] font-bold [color:var(--color-sidebar-text,#111827)] uppercase tracking-wider">Pilotage</span>
                          <ChevronDown className={`h-3 w-3 [color:var(--color-sidebar-muted,#9ca3af)] transition-transform ${meEspaceOpen.pilotage ? 'rotate-0' : '-rotate-90'}`} />
                        </button>
                        {meEspaceOpen.pilotage && (<div className="ml-2 space-y-0.5">

                        {/* Mes projets — les projets que le manager pilote ou
                            auxquels il contribue, faute d'interfaçage avec un
                            outil de gestion de projet. */}
                        {hasModule('projets') && (hasPermission('saisir_mes_projets') || hasPermission('access_projets')) && (
                          <LienMenu
                            to="/mon-espace/mes-projets"
                            onClick={fermerSiDrawer}
                            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                              location.pathname.startsWith('/mon-espace/mes-projets')
                                ? 'text-blue-700 bg-blue-50 font-medium'
                                : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                            }`}
                          >
                            <FolderKanban className="h-4 w-4" />
                            <span>Mes projets</span>
                          </LienMenu>
                        )}

                        {/* Mes collaborateurs — organigramme direct reports */}
                        {hasModule('rh') && hasPermission('access_rh') && (
                        <LienMenu
                          to="/mon-espace/mes-collaborateurs"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                            location.pathname.startsWith('/mon-espace/mes-collaborateurs')
                              ? 'text-blue-700 bg-blue-50 font-medium'
                              : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                        >
                          <Users className="h-4 w-4" />
                          <span>Mes collaborateurs</span>
                        </LienMenu>
                        )}

                        {/* Gouvernance & suivi — réunions et résolutions,
                            indépendant des campagnes d'évaluation. */}
                        {hasPermission('access_gouvernance') && (
                          <LienMenu
                            to="/gouvernance"
                            onClick={fermerSiDrawer}
                            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                              location.pathname.startsWith('/gouvernance')
                                ? 'text-blue-700 bg-blue-50 font-medium'
                                : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                            }`}
                          >
                            <ClipboardList className="h-4 w-4" />
                            <span>Gouvernance & suivi</span>
                          </LienMenu>
                        )}


                        {/* Demande de recrutement */}
                        <LienMenu
                          to="/rh/recrutement"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                            location.pathname.startsWith('/rh/recrutement')
                              ? 'text-blue-700 bg-blue-50 font-medium'
                              : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                        >
                          <UserPlus className="h-4 w-4" />
                          <span>Demande de recrutement</span>
                        </LienMenu>

                        {/* Ce que le responsable a programmé avec son équipe :
                            entretiens de campagne et réunions de gouvernance. */}
                        <LienMenu
                          to="/gouvernance?vue=entretiens"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                            location.search.includes('vue=entretiens')
                              ? 'text-blue-700 bg-blue-50 font-medium'
                              : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                        >
                          <CalendarCheck className="h-4 w-4 text-purple-500" />
                          <span>Entretiens planifiés</span>
                        </LienMenu>

                        {/* Mes objectifs */}
                        <LienMenu
                          to="/mon-espace/mes-objectifs"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                            location.pathname === '/mon-espace/mes-objectifs'
                              ? 'text-blue-700 bg-blue-50 font-medium'
                              : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                        >
                          <Target className="h-4 w-4" />
                          <span>Mes objectifs</span>
                        </LienMenu>

                        {/* Mon Équipe (N-1) */}
                        <LienMenu
                          to="/rh/mon-equipe"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                            location.pathname.startsWith('/rh/mon-equipe')
                              ? 'text-blue-700 bg-blue-50 font-medium'
                              : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                        >
                          <Users className="h-4 w-4" />
                          <span>Mon équipe</span>
                        </LienMenu>

                        {/* Mes collègues */}
                        <LienMenu
                          to="/rh/mes-collegues"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                            location.pathname.startsWith('/rh/mes-collegues')
                              ? 'text-blue-700 bg-blue-50 font-medium'
                              : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                        >
                          <Users className="h-4 w-4" />
                          <span>Mes collègues</span>
                        </LienMenu>

                        {/* Évaluations équipe */}
                        <LienMenu
                          to="/rh/evaluations"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                            location.pathname.startsWith('/rh/evaluations')
                              ? 'text-blue-700 bg-blue-50 font-medium'
                              : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                        >
                          <CheckCircle className="h-4 w-4" />
                          <span>Évaluations équipe</span>
                        </LienMenu>

                        {/* Intérims & délégation */}
                        <LienMenu
                          to="/rh/mes-delegations"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                            location.pathname.startsWith('/rh/mes-delegations')
                              ? 'text-blue-700 bg-blue-50 font-medium'
                              : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                        >
                          <ArrowRightLeft className="h-4 w-4" />
                          <span>Intérims &amp; délégation</span>
                        </LienMenu>

                        {/* Mon équipe (N-1) */}
                        <LienMenu
                          to="/rh/mon-equipe"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                            location.pathname.startsWith('/rh/mon-equipe')
                              ? 'text-blue-700 bg-blue-50 font-medium'
                              : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                        >
                          <Users className="h-4 w-4" />
                          <span>Mon équipe</span>
                        </LienMenu>

                        {/* Mes collègues */}
                        <LienMenu
                          to="/rh/mes-collegues"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                            location.pathname.startsWith('/rh/mes-collegues')
                              ? 'text-blue-700 bg-blue-50 font-medium'
                              : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                        >
                          <Users className="h-4 w-4" />
                          <span>Mes collègues</span>
                        </LienMenu>

                        {/* Annuaire */}
                        <LienMenu
                          to="/rh/annuaire"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                            location.pathname.startsWith('/rh/annuaire')
                              ? 'text-blue-700 bg-blue-50 font-medium'
                              : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                        >
                          <BookUser className="h-4 w-4" />
                          <span>Annuaire</span>
                        </LienMenu>

                        {/* Matrice compétences — bientôt */}
                        <div className="flex items-center space-x-2 p-2 text-sm rounded-lg opacity-50 cursor-default">
                          <LayoutGrid className="h-4 w-4 text-purple-500" />
                          <span className="flex-1 [color:var(--color-sidebar-muted,#9ca3af)]">Matrice compétences</span>
                          <span className="text-[9px] [color:var(--color-sidebar-muted,#9ca3af)] bg-gray-100 px-1 rounded">bientôt</span>
                        </div>
                        </div>)}
                      </>
                    )}

                    {/* ══ DOCUMENTS ENTREPRISE ════════════════════════ */}
                    {(hasModule('rh') || hasPermission('access_documents_entreprise')) && (
                      <>
                        <button
                          onClick={() => toggleMeEspace('documents')}
                          className="w-full flex items-center justify-between px-2 py-1.5 mt-2 rounded-md hover:bg-gray-100 transition-colors group"
                        >
                          <span className="text-[11px] font-bold [color:var(--color-sidebar-text,#111827)] uppercase tracking-wider">Documents entreprise</span>
                          <ChevronDown className={`h-3 w-3 [color:var(--color-sidebar-muted,#9ca3af)] transition-transform ${meEspaceOpen.documents ? 'rotate-0' : '-rotate-90'}`} />
                        </button>
                        {meEspaceOpen.documents && (<div className="ml-2 space-y-0.5">

                        <LienMenu
                          to="/rh/documents-entreprise?type=REGLEMENT_INTERIEUR"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/documents-entreprise') && location.search.includes('REGLEMENT_INTERIEUR') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                        >
                          <BookOpen className="h-4 w-4" />
                          <span className="flex-1">Règlement intérieur</span>
                        </LienMenu>

                        <LienMenu
                          to="/rh/documents-entreprise?type=CONVENTION_COLLECTIVE"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/documents-entreprise') && location.search.includes('CONVENTION_COLLECTIVE') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                        >
                          <FileText className="h-4 w-4" />
                          <span className="flex-1">Conventions collectives</span>
                        </LienMenu>

                        <LienMenu
                          to="/rh/documents-entreprise?type=NOTE_DE_SERVICE"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/documents-entreprise') && location.search.includes('NOTE_DE_SERVICE') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                        >
                          <FileText className="h-4 w-4" />
                          <span className="flex-1">Notes de service</span>
                        </LienMenu>

                        <LienMenu
                          to="/rh/documents-entreprise?type=CIRCULAIRE"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/documents-entreprise') && location.search.includes('CIRCULAIRE') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                        >
                          <Mail className="h-4 w-4" />
                          <span className="flex-1">Circulaires</span>
                        </LienMenu>

                        <LienMenu
                          to="/rh/documents-entreprise?type=MANUEL_PROCEDURE"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/documents-entreprise') && location.search.includes('MANUEL_PROCEDURE') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                        >
                          <BookOpen className="h-4 w-4" />
                          <span className="flex-1">Manuels de procédures</span>
                        </LienMenu>

                        </div>)}
                      </>
                    )}

                    {/* ══ VIE D'ENTREPRISE ════════════════════════════ */}
                    {(hasModule('rh') || hasPermission('access_annonces_rh') || hasPermission('access_evenements_rh') || hasPermission('access_enquetes_rh')) && (
                      <>
                        <button
                          onClick={() => toggleMeEspace('vie')}
                          className="w-full flex items-center justify-between px-2 py-1.5 mt-2 rounded-md hover:bg-gray-100 transition-colors group"
                        >
                          <span className="text-[11px] font-bold [color:var(--color-sidebar-text,#111827)] uppercase tracking-wider">Vie d'entreprise</span>
                          <ChevronDown className={`h-3 w-3 [color:var(--color-sidebar-muted,#9ca3af)] transition-transform ${meEspaceOpen.vie ? 'rotate-0' : '-rotate-90'}`} />
                        </button>
                        {meEspaceOpen.vie && (<div className="ml-2 space-y-0.5">

                        {/* ══ COMMUNICATION INTERNE ══════════════════════════
                            C'était un <div> sans gestionnaire de clic : le
                            titre s'affichait mais rien ne se repliait. */}
                        <button
                          onClick={() => toggleMeEspace('commInterneEspace')}
                          className="w-full flex items-center justify-between px-2 py-1 mt-3 mb-0.5 rounded-md hover:bg-black/5 transition-colors"
                        >
                          <span className="text-xs font-semibold [color:var(--color-sidebar-text,#111827)] uppercase tracking-wide">Communication Interne</span>
                          <ChevronDown className={`h-3 w-3 [color:var(--color-sidebar-muted,#9ca3af)] transition-transform ${meEspaceOpen.commInterneEspace ? 'rotate-0' : '-rotate-90'}`} />
                        </button>
                        {meEspaceOpen.commInterneEspace && (<div className="space-y-0.5">

                        {/* Enquêtes — deux écrans distincts selon le rôle.
                            `/rh/enquetes` est la CONFIGURATION (questions,
                            ciblage, destinataires, résultats) : réservée aux
                            gestionnaires. Un collaborateur y arrivait sans
                            pouvoir répondre — d'où la redirection vers son
                            espace « Mes enquêtes ». */}
                        {hasPermission('access_rh') ? (
                          <LienMenu
                            to="/rh/enquetes"
                            onClick={fermerSiDrawer}
                            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                              location.pathname.startsWith('/rh/enquetes')
                                ? 'text-blue-700 bg-blue-50 font-medium'
                                : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                            }`}
                          >
                            <BarChart2 className="h-4 w-4" />
                            <span>Enquêtes internes</span>
                          </LienMenu>
                        ) : (
                          <LienMenu
                            to="/mon-espace/mes-enquetes"
                            onClick={fermerSiDrawer}
                            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                              location.pathname.startsWith('/mon-espace/mes-enquetes')
                                ? 'text-blue-700 bg-blue-50 font-medium'
                                : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                            }`}
                          >
                            <BarChart2 className="h-4 w-4" />
                            <span>Enquêtes internes</span>
                          </LienMenu>
                        )}

                        {/* Événements */}
                        <LienMenu
                          to="/rh/evenements"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                            location.pathname.startsWith('/rh/evenements')
                              ? 'text-blue-700 bg-blue-50 font-medium'
                              : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                        >
                          <PartyPopper className="h-4 w-4 text-pink-500" />
                          <span className="flex-1">Événements</span>
                        </LienMenu>

                        {/* Annonces */}
                        <LienMenu
                          to="/rh/annonces"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                            location.pathname.startsWith('/rh/annonces')
                              ? 'text-blue-700 bg-blue-50 font-medium'
                              : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                        >
                          <Bell className="h-4 w-4 text-purple-500" />
                          <span className="flex-1">Annonces</span>
                        </LienMenu>

                        {/* Mes annonces — bientôt */}
                        <div className="flex items-center space-x-2 p-2 text-sm rounded-lg opacity-50 cursor-default">
                          <Bell className="h-4 w-4 text-violet-500" />
                          <span className="flex-1 [color:var(--color-sidebar-muted,#9ca3af)]">Mes annonces</span>
                          <span className="text-[9px] [color:var(--color-sidebar-muted,#9ca3af)] bg-gray-100 px-1 rounded">bientôt</span>
                        </div>

                        {/* Team Building */}
                        <LienMenu
                          to="/rh/team-building"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                            location.pathname.startsWith('/rh/team-building')
                              ? 'text-blue-700 bg-blue-50 font-medium'
                              : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                        >
                          <Users className="h-4 w-4" />
                          <span>Team Building</span>
                        </LienMenu>

                        {/* Mon Équipe — visible par tout employé ayant le module RH */}
                        <LienMenu
                          to="/rh/mon-equipe"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                            location.pathname.startsWith('/rh/mon-equipe')
                              ? 'text-blue-700 bg-blue-50 font-medium'
                              : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                        >
                          <Users className="h-4 w-4" />
                          <span>Mon équipe</span>
                        </LienMenu>

                        {/* Mes collègues RH */}
                        <LienMenu
                          to="/rh/mes-collegues"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                            location.pathname.startsWith('/rh/mes-collegues')
                              ? 'text-blue-700 bg-blue-50 font-medium'
                              : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                        >
                          <Users className="h-4 w-4" />
                          <span>Mes collègues</span>
                        </LienMenu>

                        {/* Annuaire / Trombinoscope */}
                        <LienMenu
                          to="/rh/annuaire"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                            location.pathname.startsWith('/rh/annuaire')
                              ? 'text-blue-700 bg-blue-50 font-medium'
                              : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                        >
                          <BookUser className="h-4 w-4" />
                          <span>{t('sidebar.monEspace.annuaireRH')}</span>
                        </LienMenu>

                        {/* Mon Dossier Médical — tout employé authentifié */}
                        <LienMenu
                          to="/rh/medical/mon-dossier"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                            location.pathname.startsWith('/rh/medical/mon-dossier')
                              ? 'text-blue-700 bg-blue-50 font-medium'
                              : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                        >
                          <Stethoscope className="h-4 w-4" />
                          <span>{t('sidebar.monEspace.monDossierMedical')}</span>
                        </LienMenu>
                        </div>)}
                        </div>)}
                      </>
                    )}

                    {/* ══ MODULE Décaissements / Workflow ═════════════ */}
                    {hasModule('decaissement_workflow') && (
                      <>
                        {/* Mes acomptes (liste) */}
                        <LienMenu
                          to="/mon-espace/acomptes"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                            location.pathname.startsWith('/mon-espace/acompte')
                              ? 'text-blue-700 bg-blue-50 font-medium'
                              : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                        >
                          <CreditCard className="h-4 w-4" />
                          <span>Mes acomptes</span>
                        </LienMenu>

                        {/* Mes prets (liste) */}
                        <LienMenu
                          to="/mon-espace/prets"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                            location.pathname.startsWith('/mon-espace/pret')
                              ? 'text-blue-700 bg-blue-50 font-medium'
                              : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                        >
                          <Banknote className="h-4 w-4" />
                          <span>Mes prets</span>
                        </LienMenu>
                      </>
                    )}

                    {/* ══ MODULE Achats ════════════════════════════════ */}
                    {hasModule('achats') && (
                      <>
                        <LienMenu
                          to="/mon-espace/achats"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                            location.pathname.startsWith('/mon-espace/achats')
                              ? 'text-blue-700 bg-blue-50 font-medium'
                              : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                        >
                          <ShoppingCart className="h-4 w-4" />
                          <span>Mes achats</span>
                        </LienMenu>
                      </>
                    )}

                    {/* ══ MODULE Vie Scolaire ══════════════════════════ */}
                    {hasModule('vie_scolaire') && hasPermission('access_vie_scolaire') && (
                      <>
                        <LienMenu
                          to="/vie-scolaire"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                            location.pathname === '/vie-scolaire'
                              ? 'text-blue-700 bg-blue-50 font-medium'
                              : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                        >
                          <GraduationCap className="h-4 w-4" />
                          <span>Vie Scolaire</span>
                        </LienMenu>
                      </>
                    )}

                    {/* ══ MODULE Immobilier ════════════════════════════ */}
                    {hasModule('immobilier') && hasPermission('access_immobilier') && (
                      <>
                        <LienMenu
                          to="/immobilier"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                            location.pathname === '/immobilier' || (location.pathname.startsWith('/immobilier/') && !location.pathname.startsWith('/immobilier/dashboard'))
                              ? 'text-blue-700 bg-blue-50 font-medium'
                              : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                        >
                          <Building2 className="h-4 w-4" />
                          <span>Ventes Immobilieres</span>
                        </LienMenu>
                        {hasPermission('view_immo_stats') && (
                          <LienMenu
                            to="/immobilier/dashboard-consolide"
                            onClick={fermerSiDrawer}
                            className={`flex items-center space-x-2 ml-6 p-1.5 text-xs rounded-lg transition-colors ${
                              location.pathname === '/immobilier/dashboard-consolide'
                                ? 'text-emerald-700 bg-emerald-50 font-semibold'
                                : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                            }`}
                          >
                            <Target className="h-3.5 w-3.5" />
                            <span>Dashboard consolidé</span>
                          </LienMenu>
                        )}
                      </>
                    )}

                    {/* ══ Cross-module — toujours visible ══════════════ */}

                    {/* Demandes de sortie d'articles */}
                    {(hasPermission('access_demande_sortie') || hasPermission('create_demande_sortie')) && (
                      <LienMenu
                        to="/stock/demandes-sortie"
                        onClick={fermerSiDrawer}
                        title="Mes demandes de sortie de matériel/articles"
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                          location.pathname.startsWith('/stock/demandes-sortie')
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                        }`}
                      >
                        <Package className="h-4 w-4" />
                        <span>Sortie Matériel</span>
                      </LienMenu>
                    )}

                    {/* Portail des Applications */}
                    <LienMenu
                      to="/mon-espace/portail-applications"
                      onClick={fermerSiDrawer}
                      className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                        location.pathname.startsWith('/mon-espace/portail-applications')
                          ? 'text-blue-700 bg-blue-50 font-medium'
                          : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                      }`}
                    >
                      <Grid3x3 className="h-4 w-4" />
                      <span>Portail des Applications</span>
                    </LienMenu>

                    {/* Types d'opération dynamiques (DevMode) ciblant mon_espace */}
                    <SidebarDynamicTypes
                      moduleCode="mon_espace"
                      closeMobile={() => setSidebarOpen(false)} />
                  </div>
                )}

                {/* ── Sous-menu RH admin — groupes accordion ─────────────────────── */}
                {sidebarOpen && hasSubmenu && isOpen && item.id === 'rh' && (
                  <div className="ml-8 mt-1 mb-2 space-y-1 border-l-2 border-gray-200 pl-2">

                    {/* ══ GESTION CAPITAL HUMAIN ══════════════════════════ */}
                    {/* Le titre du groupe ne s’affiche que si au moins une de ses
                        entrées l’est. Sans cela, un utilisateur sans droit de paie
                        voyait « Paie & Rémunération » ouvrir sur du vide — un menu
                        qui promet ce qu’il ne peut pas tenir. */}
                    {(hasPermission('access_admin') || hasPermission('access_contrats') || hasPermission('access_employes') || hasPermission('access_evaluation_performance') || hasPermission('access_recrutement') || hasPermission('access_rh') || hasPermission('admin_workflows') || hasPermission('declarer_conflit_interet') || hasPermission('manage_rh_params') || hasPermission('proposer_avancement') || hasPermission('proposer_distinction') || hasPermission('acces_conformite_dossier') || hasPermission('gerer_campagnes_documents')) && (
                      <>
                    <button
                      onClick={() => toggleRh('capital')}
                      className="w-full flex items-center justify-between px-2 py-1 mt-3 mb-0.5 rounded-md hover:bg-black/5 transition-colors"
                    >
                      <span className="text-xs font-semibold [color:var(--color-sidebar-text,#111827)] uppercase tracking-wide">Gestion Capital Humain</span>
                      <ChevronDown className={`h-3 w-3 [color:var(--color-sidebar-muted,#9ca3af)] transition-transform ${rhOpen.capital ? 'rotate-0' : '-rotate-90'}`} />
                    </button>
                    {rhOpen.capital && (
                      <div className="ml-2 space-y-0.5">
                        {/* TDB RH */}
                        <LienMenu
                          to="/rh/dashboard"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname === '/rh/dashboard' ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                        >
                          <LayoutDashboard className="h-4 w-4" />
                          <span>{t('sidebar.rh.tableauDeBordRH')}</span>
                        </LienMenu>
                        {hasPermission('access_rh') && (
                          <LienMenu
                            to="/rh/tableau-de-bord"
                            onClick={fermerSiDrawer}
                            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname === '/rh/tableau-de-bord' ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                          >
                            <BarChart3 className="h-4 w-4" />
                            <span>Analyses RH (masse salariale)</span>
                          </LienMenu>
                        )}
                        {/* Paramètres RH — visibilité pilotée par le droit
                            `manage_rh_params`, attribuable profil par profil
                            (catégorie « RH — Paramètres »). */}
                        {hasPermission('manage_rh_params') && (
                          <LienMenu
                            to="/rh/parametres"
                            onClick={fermerSiDrawer}
                            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/parametres') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                          >
                            <Settings className="h-4 w-4" />
                            <span>Paramètres RH</span>
                          </LienMenu>
                        )}
                        {/* Workflow RH — onglet d'Administration, atteint
                            directement via `?tab=workflow_rh`. Même droit que
                            l'onglet lui-même (`admin_workflows`). */}
                        {(hasPermission('admin_workflows') || hasPermission('access_admin')) && (
                          <LienMenu
                            to="/administration?tab=workflow_rh"
                            onClick={fermerSiDrawer}
                            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname === '/administration' && location.search.includes('workflow_rh') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                          >
                            <GitBranch className="h-4 w-4" />
                            <span>Workflow RH</span>
                          </LienMenu>
                        )}
                        {/* Annuaire / Employés */}
                        {hasPermission('access_employes') && (
                          <LienMenu
                            to="/rh/employes"
                            onClick={fermerSiDrawer}
                            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/employes') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                          >
                            <Users className="h-4 w-4" />
                            <span>{t('sidebar.rh.employes')}</span>
                          </LienMenu>
                        )}
                        {hasPermission('access_rh') && (
                          <LienMenu
                            to="/rh/annuaire"
                            onClick={fermerSiDrawer}
                            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/annuaire') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                          >
                            <BookUser className="h-4 w-4" />
                            <span>Annuaire</span>
                          </LienMenu>
                        )}
                        {/* Organigramme / Hiérarchie */}
                        {hasPermission('access_rh') && (
                          <LienMenu
                            to="/rh/organisation"
                            onClick={fermerSiDrawer}
                            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/organisation') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                          >
                            <Building2 className="h-4 w-4" />
                            <span>{t('sidebar.rh.organisation')}</span>
                          </LienMenu>
                        )}
                        {/* Contrats */}
                        {hasPermission('access_contrats') && (
                          <LienMenu
                            to="/rh/contrats"
                            onClick={fermerSiDrawer}
                            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${(location.pathname === '/rh/contrats' || (location.pathname.startsWith('/rh/contrats/') && !location.pathname.startsWith('/rh/contrats/import'))) ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                          >
                            <FileText className="h-4 w-4" />
                            <span>{t('sidebar.rh.contrats')}</span>
                          </LienMenu>
                        )}
                        {/* L'import PDF a son propre droit : consulter les
                            contrats et en créer un par lecture automatique d'un
                            document ne sont pas la même responsabilité. */}
                        {hasPermission('import_contrat_rh') && (
                          <LienMenu
                            to="/rh/contrats/import"
                            onClick={fermerSiDrawer}
                            className={`flex items-center space-x-2 p-2 pl-6 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/contrats/import') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                          >
                            <Upload className="h-4 w-4" />
                            <span>Importer un contrat (PDF)</span>
                          </LienMenu>
                        )}
                        {/* Évaluations */}
                        {hasPermission('access_rh') && (
                          <LienMenu
                            to="/rh/evaluations"
                            onClick={fermerSiDrawer}
                            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/evaluations') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                          >
                            <CheckCircle className="h-4 w-4" />
                            <span>{t('sidebar.rh.evaluations')}</span>
                          </LienMenu>
                        )}
                        {hasPermission('access_evaluation_performance') && (
                          <LienMenu
                            to="/evaluation-performance"
                            onClick={fermerSiDrawer}
                            title="Objectifs, auto-évaluation, évaluation manager et pilotage RH des campagnes"
                            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/evaluation-performance') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                          >
                            <Target className="h-4 w-4" />
                            <span>Évaluation &amp; Performance</span>
                          </LienMenu>
                        )}
                        {/* Conflits d'intérêts.
                            Gardé par le droit de DÉCLARER, le plus large :
                            chaque collaborateur dépose ses propres
                            déclarations. Le référent déontologie y trouve en
                            plus le registre — c'est le backend qui fait la
                            différence, pas l'affichage du menu. */}
                        {hasPermission('declarer_conflit_interet') && (
                          <LienMenu
                            to="/conflits-interet"
                            onClick={fermerSiDrawer}
                            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/conflits-interet') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                          >
                            <ShieldCheck className="h-4 w-4" />
                            <span>Conflits d'intérêts</span>
                          </LienMenu>
                        )}
                        {/* Avancements — la GESTION des campagnes.
                            Gardé par le droit de PROPOSER, le plus large : un encadrant
                            doit voir l'entrée pour déposer. L'arbitrage, l'application
                            et le tableau des coûts — qui montre les salaires proposés —
                            restent gardés dans le contrôleur par `gerer_avancements`.
                            L'agent, lui, suit son propre déroulé depuis « Mes
                            avancements », dans Mon espace. */}
                        {hasPermission('proposer_avancement') && (
                          <LienMenu
                            to="/rh/avancements"
                            onClick={fermerSiDrawer}
                            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/avancements') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                          >
                            <TrendingUp className="h-4 w-4" />
                            <span>Avancements</span>
                          </LienMenu>
                        )}
                        {/* Distinctions honorifiques — référentiel, propositions, contingents.
                            Même partage : proposer ici, consulter les siennes dans
                            Mon espace. */}
                        {hasPermission('proposer_distinction') && (
                          <LienMenu
                            to="/rh/distinctions"
                            onClick={fermerSiDrawer}
                            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/distinctions') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                          >
                            <Award className="h-4 w-4" />
                            <span>Distinctions</span>
                          </LienMenu>
                        )}
                        {/* Conformité des dossiers du personnel.
                            Gardée par le droit de CONSULTER, le plus large : un
                            gestionnaire RH doit voir ce qui manque. Ouvrir une
                            campagne et relancer restent gardés dans le contrôleur. */}
                        {(hasPermission('acces_conformite_dossier') || hasPermission('gerer_campagnes_documents') || hasPermission('access_employes')) && (
                          <LienMenu
                            to="/rh/conformite-dossiers"
                            onClick={fermerSiDrawer}
                            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/conformite-dossiers') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                          >
                            <FolderCheck className="h-4 w-4" />
                            <span>Conformité des dossiers</span>
                          </LienMenu>
                        )}
                        {/* Petit matériel — biens remis aux agents mais passés en
                            charge, et seuil d'immobilisation. */}
                        {(hasPermission('gerer_petit_materiel') || hasPermission('access_employes')) && (
                          <LienMenu
                            to="/rh/petit-materiel"
                            onClick={fermerSiDrawer}
                            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/petit-materiel') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                          >
                            <Package className="h-4 w-4" />
                            <span>Petit matériel</span>
                          </LienMenu>
                        )}
                        {/* Plan carrière / Délégations */}
                        {hasPermission('access_rh') && (
                          <LienMenu
                            to="/rh/delegations"
                            onClick={fermerSiDrawer}
                            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/delegations') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                          >
                            <ArrowRightLeft className="h-4 w-4" />
                            <span>Délégations &amp; Intérims</span>
                          </LienMenu>
                        )}
                        {/* Demandes RH */}
                        {hasPermission('access_rh') && (
                          <LienMenu
                            to="/rh/demandes"
                            onClick={fermerSiDrawer}
                            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/demandes') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                          >
                            <Inbox className="h-4 w-4" />
                            <span>{t('sidebar.rh.demandesRH')}</span>
                          </LienMenu>
                        )}
                        {/* Recrutement */}
                        {hasPermission('access_recrutement') && (
                          <LienMenu
                            to="/rh/recrutement"
                            onClick={fermerSiDrawer}
                            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/recrutement') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                          >
                            <Briefcase className="h-4 w-4" />
                            <span>{t('sidebar.rh.recrutement')}</span>
                          </LienMenu>
                        )}
                      </div>
                    )}
                      </>
                    )}

                    {/* ══ DOCUMENTS D'ENTREPRISE ══════════════════════════ */}
                    {(hasPermission('access_rh') || hasPermission('access_documents_entreprise')) && (
                      <>
                        <button
                          onClick={() => toggleRh('docsEntreprise')}
                          className="w-full flex items-center justify-between px-2 py-1 mt-3 mb-0.5 rounded-md hover:bg-black/5 transition-colors"
                        >
                          <span className="text-xs font-semibold [color:var(--color-sidebar-text,#111827)] uppercase tracking-wide">Documents d'entreprise</span>
                          <ChevronDown className={`h-3 w-3 [color:var(--color-sidebar-muted,#9ca3af)] transition-transform ${rhOpen.docsEntreprise ? 'rotate-0' : '-rotate-90'}`} />
                        </button>
                        {rhOpen.docsEntreprise && (
                          <div className="ml-2 space-y-0.5">
                            <LienMenu
                              to="/rh/documents-entreprise?type=REGLEMENT_INTERIEUR"
                              onClick={fermerSiDrawer}
                              className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/documents-entreprise') && location.search.includes('REGLEMENT_INTERIEUR') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                            >
                              <BookOpen className="h-4 w-4" />
                              <span className="flex-1">Règlement intérieur</span>
                            </LienMenu>
                            <LienMenu
                              to="/rh/documents-entreprise?type=CONVENTION_COLLECTIVE"
                              onClick={fermerSiDrawer}
                              className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/documents-entreprise') && location.search.includes('CONVENTION_COLLECTIVE') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                            >
                              <FileText className="h-4 w-4" />
                              <span className="flex-1">Conventions collectives</span>
                            </LienMenu>
                            <LienMenu
                              to="/rh/documents-entreprise?type=NOTE_DE_SERVICE"
                              onClick={fermerSiDrawer}
                              className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/documents-entreprise') && location.search.includes('NOTE_DE_SERVICE') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                            >
                              <FileText className="h-4 w-4" />
                              <span className="flex-1">Notes de service</span>
                            </LienMenu>
                            <LienMenu
                              to="/rh/documents-entreprise?type=CIRCULAIRE"
                              onClick={fermerSiDrawer}
                              className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/documents-entreprise') && location.search.includes('CIRCULAIRE') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                            >
                              <Mail className="h-4 w-4" />
                              <span className="flex-1">Circulaires</span>
                            </LienMenu>
                            <LienMenu
                              to="/rh/documents-entreprise?type=MANUEL_PROCEDURE"
                              onClick={fermerSiDrawer}
                              className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/documents-entreprise') && location.search.includes('MANUEL_PROCEDURE') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                            >
                              <BookOpen className="h-4 w-4" />
                              <span className="flex-1">Manuels de procédures</span>
                            </LienMenu>
                          </div>
                        )}
                      </>
                    )}

                    {/* ══ PAIE & RÉMUNÉRATION ══════════════════════════════ */}
                    {/* Le titre du groupe ne s’affiche que si au moins une de ses
                        entrées l’est. Sans cela, un utilisateur sans droit de paie
                        voyait « Paie & Rémunération » ouvrir sur du vide — un menu
                        qui promet ce qu’il ne peut pas tenir. */}
                    {(hasPermission('access_paie') || hasPermission('view_remuneration_mandataires')) && (
                      <>
                    <button
                      onClick={() => toggleRh('paie')}
                      className="w-full flex items-center justify-between px-2 py-1 mt-3 mb-0.5 rounded-md hover:bg-black/5 transition-colors"
                    >
                      <span className="text-xs font-semibold [color:var(--color-sidebar-text,#111827)] uppercase tracking-wide">Paie &amp; Rémunération</span>
                      <ChevronDown className={`h-3 w-3 [color:var(--color-sidebar-muted,#9ca3af)] transition-transform ${rhOpen.paie ? 'rotate-0' : '-rotate-90'}`} />
                    </button>
                    {rhOpen.paie && (
                      <div className="ml-2 space-y-0.5">
                        {hasPermission('access_paie') && (
                          <LienMenu
                            to="/rh/paie"
                            onClick={fermerSiDrawer}
                            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/paie') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                          >
                            <DollarSign className="h-4 w-4" />
                            <span>{t('sidebar.rh.paie')}</span>
                          </LienMenu>
                        )}
                        {hasPermission('access_paie') && (
                          <LienMenu
                            to="/rh/campagnes-primes"
                            onClick={fermerSiDrawer}
                            title="Campagnes collectives ou ciblées (résultats, objectifs, exceptionnelles)"
                            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/campagnes-primes') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                          >
                            <Gift className="h-4 w-4" />
                            <span>Campagnes de primes</span>
                          </LienMenu>
                        )}
                        {hasPermission('access_paie') && (
                          <LienMenu
                            to="/rh/bordereaux-journaliers"
                            onClick={fermerSiDrawer}
                            title="Paie hebdomadaire des journaliers (cash)"
                            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/bordereaux-journaliers') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                          >
                            <Users className="h-4 w-4" />
                            <span>Paie journaliers</span>
                          </LienMenu>
                        )}
                        {hasPermission('access_paie') && (
                          <LienMenu
                            to="/rh/bordereaux-stages"
                            onClick={fermerSiDrawer}
                            title="Paiement d'allocations de stage"
                            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/bordereaux-stages') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                          >
                            <GraduationCap className="h-4 w-4" />
                            <span>Paie stages</span>
                          </LienMenu>
                        )}
                        {hasPermission('view_remuneration_mandataires') && (
                          <LienMenu
                            to="/rh/remuneration-mandataires"
                            onClick={fermerSiDrawer}
                            title="Rémunération des gérants non salariés (SARL U) — IRG + CN, sans CNPS"
                            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/remuneration-mandataires') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                          >
                            <Crown className="h-4 w-4" />
                            <span>Rémunération mandataires</span>
                          </LienMenu>
                        )}
                      </div>
                    )}
                      </>
                    )}

                    {/* ══ RELATIONS SOCIALES ══════════════════════════════ */}
                    {hasPermission('access_contrats') && (
                      <>
                        {/* Le titre du groupe ne s’affiche que si au moins une de ses
                            entrées l’est. Sans cela, un utilisateur sans droit de paie
                            voyait « Paie & Rémunération » ouvrir sur du vide — un menu
                            qui promet ce qu’il ne peut pas tenir. */}
                        {(hasPermission('access_conflits_sociaux') || hasPermission('access_contentieux') || hasPermission('access_syndicats')) && (
                          <>
                        <button
                          onClick={() => toggleRh('relationsSociales')}
                          className="w-full flex items-center justify-between px-2 py-1 mt-3 mb-0.5 rounded-md hover:bg-black/5 transition-colors"
                        >
                          <span className="text-xs font-semibold [color:var(--color-sidebar-text,#111827)] uppercase tracking-wide">Relations sociales</span>
                          <ChevronDown className={`h-3 w-3 [color:var(--color-sidebar-muted,#9ca3af)] transition-transform ${rhOpen.relationsSociales ? 'rotate-0' : '-rotate-90'}`} />
                        </button>
                        {rhOpen.relationsSociales && (
                        <div className="ml-2 space-y-0.5">
                          <LienMenu
                            to="/rh/organismes-sociaux"
                            onClick={fermerSiDrawer}
                            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/organismes-sociaux') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                          >
                            <Building2 className="h-4 w-4" />
                            <span>{t('sidebar.rh.organismesSociaux')}</span>
                          </LienMenu>

                          {/* Syndicats, conflits collectifs et contentieux :
                              la page filtre ses onglets selon les droits. */}
                          {(hasPermission('access_syndicats')
                            || hasPermission('access_conflits_sociaux')
                            || hasPermission('access_contentieux')) && (
                            <LienMenu
                              to="/rh/relations-sociales"
                              onClick={fermerSiDrawer}
                              className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/relations-sociales') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                            >
                              <Users className="h-4 w-4" />
                              <span>Syndicats, conflits & contentieux</span>
                            </LienMenu>
                          )}
                        </div>
                        )}
                          </>
                        )}
                      </>
                    )}

                    {/* ══ PRÉSENCE & TEMPS ════════════════════════════════ */}
                    {/* Le titre du groupe ne s’affiche que si au moins une de ses
                        entrées l’est. Sans cela, un utilisateur sans droit de paie
                        voyait « Paie & Rémunération » ouvrir sur du vide — un menu
                        qui promet ce qu’il ne peut pas tenir. */}
                    {(hasPermission('access_conges') || hasPermission('access_rh')) && (
                      <>
                    <button
                      onClick={() => toggleRh('presence')}
                      className="w-full flex items-center justify-between px-2 py-1 mt-3 mb-0.5 rounded-md hover:bg-black/5 transition-colors"
                    >
                      <span className="text-xs font-semibold [color:var(--color-sidebar-text,#111827)] uppercase tracking-wide">Présence &amp; Temps</span>
                      <ChevronDown className={`h-3 w-3 [color:var(--color-sidebar-muted,#9ca3af)] transition-transform ${rhOpen.presence ? 'rotate-0' : '-rotate-90'}`} />
                    </button>
                    {rhOpen.presence && (
                      <div className="ml-2 space-y-0.5">
                        {hasPermission('access_rh') && (
                          <LienMenu
                            to="/rh/pointage"
                            onClick={fermerSiDrawer}
                            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/pointage') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                          >
                            <Clock className="h-4 w-4" />
                            <span>Pointage</span>
                          </LienMenu>
                        )}
                        {hasPermission('access_conges') && (
                          <LienMenu
                            to="/rh/conges"
                            onClick={fermerSiDrawer}
                            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/conges') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                          >
                            <Calendar className="h-4 w-4" />
                            <span>{t('sidebar.rh.demandesConges')}</span>
                          </LienMenu>
                        )}
                        {hasPermission('access_conges') && (
                          <LienMenu
                            to="/rh/absences"
                            onClick={fermerSiDrawer}
                            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/absences') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                          >
                            <UserX className="h-4 w-4" />
                            <span>{t('sidebar.rh.demandesAbsence')}</span>
                          </LienMenu>
                        )}
                        {hasPermission('access_rh') && (
                          <LienMenu
                            to="/rh/disponibilite"
                            onClick={fermerSiDrawer}
                            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/disponibilite') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                          >
                            <Briefcase className="h-4 w-4" />
                            <span>{t('sidebar.rh.miseADisponibilite')}</span>
                          </LienMenu>
                        )}
                      </div>
                    )}
                      </>
                    )}

                    {/* ══ FORMATION & DEV. ════════════════════════════════ */}
                    {/* Le titre du groupe ne s’affiche que si au moins une de ses
                        entrées l’est. Sans cela, un utilisateur sans droit de paie
                        voyait « Paie & Rémunération » ouvrir sur du vide — un menu
                        qui promet ce qu’il ne peut pas tenir. */}
                    {(hasPermission('access_rh')) && (
                      <>
                    <button
                      onClick={() => toggleRh('formation')}
                      className="w-full flex items-center justify-between px-2 py-1 mt-3 mb-0.5 rounded-md hover:bg-black/5 transition-colors"
                    >
                      <span className="text-xs font-semibold [color:var(--color-sidebar-text,#111827)] uppercase tracking-wide">Formation &amp; Dev.</span>
                      <ChevronDown className={`h-3 w-3 [color:var(--color-sidebar-muted,#9ca3af)] transition-transform ${rhOpen.formation ? 'rotate-0' : '-rotate-90'}`} />
                    </button>
                    {rhOpen.formation && (
                      <div className="ml-2 space-y-0.5">
                        {hasPermission('access_rh') && (
                          <LienMenu
                            to="/rh/formations"
                            onClick={fermerSiDrawer}
                            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/formations') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                          >
                            <GraduationCap className="h-4 w-4" />
                            <span>{t('sidebar.rh.formations')}</span>
                          </LienMenu>
                        )}
                        {hasPermission('access_rh') && (
                          <LienMenu
                            to="/rh/team-building"
                            onClick={fermerSiDrawer}
                            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/team-building') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                          >
                            <Users className="h-4 w-4" />
                            <span>{t('sidebar.rh.teamBuilding')}</span>
                          </LienMenu>
                        )}
                      </div>
                    )}
                      </>
                    )}

                    {/* ══ DISCIPLINAIRE & SORTIE ══════════════════════════ */}
                    {/* Le titre du groupe ne s’affiche que si au moins une de ses
                        entrées l’est. Sans cela, un utilisateur sans droit de paie
                        voyait « Paie & Rémunération » ouvrir sur du vide — un menu
                        qui promet ce qu’il ne peut pas tenir. */}
                    {(hasPermission('access_disciplinaire') || hasPermission('access_employes')) && (
                      <>
                    <button
                      onClick={() => toggleRh('disciplinaire')}
                      className="w-full flex items-center justify-between px-2 py-1 mt-3 mb-0.5 rounded-md hover:bg-black/5 transition-colors"
                    >
                      <span className="text-xs font-semibold [color:var(--color-sidebar-text,#111827)] uppercase tracking-wide">Disciplinaire &amp; Sortie</span>
                      <ChevronDown className={`h-3 w-3 [color:var(--color-sidebar-muted,#9ca3af)] transition-transform ${rhOpen.disciplinaire ? 'rotate-0' : '-rotate-90'}`} />
                    </button>
                    {rhOpen.disciplinaire && (
                      <div className="ml-2 space-y-0.5">

                        {/* Mesures & discipline — sanctions, procédures
                            contradictoires, départs négociés, économique. */}
                        {hasPermission('access_disciplinaire') && (
                          <LienMenu
                            to="/rh/mesures"
                            onClick={fermerSiDrawer}
                            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                              location.pathname.startsWith('/rh/mesures')
                                ? 'text-blue-700 bg-blue-50 font-medium'
                                : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                            }`}
                          >
                            <Shield className="h-4 w-4" />
                            <span>Mesures & discipline</span>
                          </LienMenu>
                        )}
                        {hasPermission('access_employes') && (
                          <LienMenu
                            to="/rh/disciplinaire"
                            onClick={fermerSiDrawer}
                            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/disciplinaire') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                          >
                            <Shield className="h-4 w-4" />
                            <span>{t('sidebar.rh.procedureDisciplinaire')}</span>
                          </LienMenu>
                        )}
                        {hasPermission('access_employes') && (
                          <LienMenu
                            to="/rh/demissions"
                            onClick={fermerSiDrawer}
                            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/demissions') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                          >
                            <LogOut className="h-4 w-4" />
                            <span>{t('sidebar.rh.demissions')}</span>
                          </LienMenu>
                        )}
                      </div>
                    )}
                      </>
                    )}

                    {/* ══ ÉTATS & RAPPORTS ════════════════════════════════ */}
                    {/* Le titre du groupe ne s’affiche que si au moins une de ses
                        entrées l’est. Sans cela, un utilisateur sans droit de paie
                        voyait « Paie & Rémunération » ouvrir sur du vide — un menu
                        qui promet ce qu’il ne peut pas tenir. */}
                    {(hasPermission('acces_medecine_travail') || hasPermission('access_dossier_medical') || hasPermission('access_rh') || hasPermission('declarer_accident_travail') || hasPermission('gerer_sst') || hasPermission('voir_arrets_maladie')) && (
                      <>
                    <button
                      onClick={() => toggleRh('etats')}
                      className="w-full flex items-center justify-between px-2 py-1 mt-3 mb-0.5 rounded-md hover:bg-black/5 transition-colors"
                    >
                      <span className="text-xs font-semibold [color:var(--color-sidebar-text,#111827)] uppercase tracking-wide">États &amp; Rapports</span>
                      <ChevronDown className={`h-3 w-3 [color:var(--color-sidebar-muted,#9ca3af)] transition-transform ${rhOpen.etats ? 'rotate-0' : '-rotate-90'}`} />
                    </button>
                    {rhOpen.etats && (
                      <div className="ml-2 space-y-0.5">
                        {hasPermission('access_rh') && (
                          <LienMenu
                            to="/rh/etats"
                            onClick={fermerSiDrawer}
                            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/etats') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                          >
                            <FileBarChart className="h-4 w-4" />
                            <span>États &amp; Rapports RH</span>
                          </LienMenu>
                        )}
                        {hasPermission('access_rh') && (
                          <LienMenu
                            to="/rh/alertes"
                            onClick={fermerSiDrawer}
                            className={`flex items-center justify-between p-2 text-sm rounded-lg transition-colors ${location.pathname === '/rh/alertes' ? 'text-blue-700 bg-blue-50 font-medium' : alertesRHCount > 0 ? 'text-red-600 hover:bg-red-50' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                          >
                            <div className="flex items-center space-x-2">
                              <div className="relative">
                                <Bell className={`h-4 w-4 ${alertesRHCount > 0 ? 'animate-[swing_1s_ease-in-out_infinite]' : ''}`} />
                                {alertesRHCount > 0 && (
                                  <span className="absolute -top-1.5 -right-1.5 w-2 h-2 bg-red-500 rounded-full animate-ping" />
                                )}
                              </div>
                              <span>Alertes RH</span>
                            </div>
                            {alertesRHCount > 0 && (
                              <span className="bg-red-500 text-white text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[20px] text-center animate-pulse">
                                {alertesRHCount}
                              </span>
                            )}
                          </LienMenu>
                        )}
                        {hasPermission('access_rh') && (
                          <LienMenu
                            to="/rh/demandes-documents"
                            onClick={fermerSiDrawer}
                            title="Demandes de documents : certificat de travail, attestation d'emploi, déclaration CNPS, duplicata bulletin de paie"
                            className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/demandes-documents') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                          >
                            <FileText className="h-4 w-4" />
                            <span>Demandes documents</span>
                          </LienMenu>
                        )}
                        
                        {/* Types d'opération dynamiques (DevMode) ciblant rh */}
                        <SidebarDynamicTypes
                          moduleCode="rh"
                          closeMobile={() => setSidebarOpen(false)} />
                      </div>
                    )}
                      </>
                    )}

                    {/* ══ MÉDECINE DU TRAVAIL ═══════════════════════════
                        Le poste du médecin d'entreprise, réuni en un seul
                        endroit : les dossiers qu'il tient, les arrêts qu'il
                        suit, les visites qu'il conduit, les accidents qu'il
                        instruit. Ces écrans étaient dispersés dans « États &
                        Rapports », entre des tableaux de bord et des états
                        comptables — un médecin devait les y chercher un à un.

                        « Mon dossier médical » n'y figure pas : c'est le
                        dossier personnel de l'utilisateur, il vit dans le
                        self-service, avec ses congés et ses bulletins. */}
                    {(hasPermission('acces_medecine_travail') || hasPermission('gerer_sst')
                      || hasPermission('declarer_accident_travail')
                      || hasPermission('access_dossier_medical')
                      || hasPermission('voir_arrets_maladie')) && (
                      <>
                        <button
                          onClick={() => toggleRh('medecine')}
                          className="w-full flex items-center justify-between px-2 py-1 mt-3 mb-0.5 rounded-md hover:bg-black/5 transition-colors"
                        >
                          <span className="text-xs font-semibold [color:var(--color-sidebar-text,#111827)] uppercase tracking-wide">Médecine du travail</span>
                          <ChevronDown className={`h-3 w-3 [color:var(--color-sidebar-muted,#9ca3af)] transition-transform ${rhOpen.medecine ? 'rotate-0' : '-rotate-90'}`} />
                        </button>
                        {rhOpen.medecine && (
                          <div className="ml-2 space-y-0.5">
                            {(hasPermission('access_dossier_medical') || hasPermission('acces_medecine_travail')) && (
                              <LienMenu
                                to="/rh/medical"
                                onClick={fermerSiDrawer}
                                className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname === '/rh/medical' ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                              >
                                <Stethoscope className="h-4 w-4" />
                                <span>Dossiers médicaux</span>
                              </LienMenu>
                            )}
                            {/* Les consultations se saisissent DANS le dossier
                                de l'employé : elles n'ont pas d'écran propre,
                                une consultation hors dossier n'aurait pas de
                                sujet. */}
                            {(hasPermission('voir_arrets_maladie') || hasPermission('acces_medecine_travail')
                              || hasPermission('access_dossier_medical')) && (
                              <LienMenu
                                to="/rh/medical/arrets"
                                onClick={fermerSiDrawer}
                                className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname === '/rh/medical/arrets' ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                              >
                                <FileText className="h-4 w-4" />
                                <span>Arrêts maladie</span>
                              </LienMenu>
                            )}
                            {(hasPermission('acces_medecine_travail') || hasPermission('gerer_sst')) && (
                              <LienMenu
                                to="/rh/sante-securite?onglet=visites"
                                onClick={fermerSiDrawer}
                                className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname + location.search === '/rh/sante-securite?onglet=visites' ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                              >
                                <HeartPulse className="h-4 w-4" />
                                <span>Visites médicales</span>
                              </LienMenu>
                            )}
                            {(hasPermission('declarer_accident_travail') || hasPermission('gerer_sst')
                              || hasPermission('acces_medecine_travail')) && (
                              <LienMenu
                                to="/rh/sante-securite"
                                onClick={fermerSiDrawer}
                                className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname + location.search === '/rh/sante-securite' ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                              >
                                <HardHat className="h-4 w-4" />
                                <span>Accidents du travail</span>
                              </LienMenu>
                            )}
                            {hasPermission('gerer_sst') && (
                              <LienMenu
                                to="/rh/sante-securite?onglet=risques"
                                onClick={fermerSiDrawer}
                                className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname + location.search === '/rh/sante-securite?onglet=risques' ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                              >
                                <ShieldCheck className="h-4 w-4" />
                                <span>Document unique</span>
                              </LienMenu>
                            )}
                          </div>
                        )}
                      </>
                    )}

                    {/* ══ GPEC / GEPP ══════════════════════════════════════ */}
                    <button
                      onClick={() => toggleRh('gpecGepp')}
                      className="w-full flex items-center justify-between px-2 py-1 mt-3 mb-0.5 rounded-md hover:bg-black/5 transition-colors"
                    >
                      <span className="text-xs font-semibold [color:var(--color-sidebar-text,#111827)] uppercase tracking-wide">GPEC / GEPP</span>
                      <ChevronDown className={`h-3 w-3 [color:var(--color-sidebar-muted,#9ca3af)] transition-transform ${rhOpen.gpecGepp ? 'rotate-0' : '-rotate-90'}`} />
                    </button>
                    {rhOpen.gpecGepp && (
                      <div className="ml-2 space-y-0.5">

                        {/* Fiches de postes */}
                        <LienMenu
                          to="/rh/gpec/fiches-postes"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                            location.pathname.startsWith('/rh/gpec/fiches-postes')
                              ? 'text-blue-700 bg-blue-50 font-medium'
                              : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                        >
                          <Briefcase className="h-4 w-4" />
                          <span className="flex-1">Fiches de postes</span>
                        </LienMenu>

                        {/* Matrice compétences */}
                        <LienMenu
                          to="/rh/gpec/matrice-competences"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                            location.pathname.startsWith('/rh/gpec/matrice-competences')
                              ? 'text-blue-700 bg-blue-50 font-medium'
                              : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                        >
                          <LayoutGrid className="h-4 w-4" />
                          <span className="flex-1">Matrice compétences</span>
                        </LienMenu>

                        {/* Profils de carrière */}
                        <LienMenu
                          to="/rh/gpec/profils-carriere"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                            location.pathname.startsWith('/rh/gpec/profils-carriere')
                              ? 'text-blue-700 bg-blue-50 font-medium'
                              : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}
                        >
                          <BookUser className="h-4 w-4" />
                          <span className="flex-1">Profils de carrière</span>
                        </LienMenu>

                        {/* Plans de succession */}
                        <LienMenu to="/rh/gpec/succession" className="flex items-center space-x-2 p-2 text-sm rounded-lg hover:bg-teal-50 text-gray-700 hover:text-teal-700">
                          <Users className="h-4 w-4 text-teal-500" />
                          <span className="flex-1">Plans de succession</span>
                        </LienMenu>

                        {/* Talents & Nine Box */}
<LienMenu to="/rh/gpec/talents" className="flex items-center space-x-2 p-2 text-sm rounded-lg hover:bg-teal-50 text-gray-700 hover:text-teal-700">
                          <Grid3x3 className="h-4 w-4 text-teal-500" />
                          <span className="flex-1">Talents & Nine Box</span>
                        </LienMenu>

                        {/* Matrice des risques RH */}
<LienMenu to="/rh/gpec/risques" className="flex items-center space-x-2 p-2 text-sm rounded-lg hover:bg-teal-50 text-gray-700 hover:text-teal-700">
                          <ShieldAlert className="h-4 w-4 text-teal-500" />
                          <span className="flex-1">Matrice des risques RH</span>
                        </LienMenu>

                        {/* Feedback 360° */}
                        <LienMenu to="/rh/gpec/feedback-360" className="flex items-center space-x-2 p-2 text-sm rounded-lg hover:bg-teal-50 text-gray-700 hover:text-teal-700">
                          <BarChart2 className="h-4 w-4 text-teal-500" />
                          <span className="flex-1">Feedback 360°</span>
                        </LienMenu>

                        {/* Mobilité et carrière */}
                        <LienMenu to="/rh/gpec/mobilite-interne" className="flex items-center space-x-2 p-2 text-sm rounded-lg hover:bg-teal-50 text-gray-700 hover:text-teal-700">
                          <TrendingUp className="h-4 w-4 text-teal-500" />
                          <span className="flex-1">Mobilité et carrière</span>
                        </LienMenu>

                        {/* États & Rapports GPEC */}
                        <LienMenu to="/rh/gpec/etats" className="flex items-center space-x-2 p-2 text-sm rounded-lg hover:bg-teal-50 text-gray-700 hover:text-teal-700">
                          <FileSpreadsheet className="h-4 w-4 text-teal-500" />
                          <span className="flex-1">États & Rapports</span>
                        </LienMenu>

                      </div>
                    )}

                    {/* ══ COMMUNICATION INTERNE ════════════════════════════ */}
                    <button
                      onClick={() => toggleRh('commInterne')}
                      className="w-full flex items-center justify-between px-2 py-1 mt-3 mb-0.5 rounded-md hover:bg-black/5 transition-colors"
                    >
                      <span className="text-xs font-semibold [color:var(--color-sidebar-text,#111827)] uppercase tracking-wide">Communication Interne</span>
                      <ChevronDown className={`h-3 w-3 [color:var(--color-sidebar-muted,#9ca3af)] transition-transform ${rhOpen.commInterne ? 'rotate-0' : '-rotate-90'}`} />
                    </button>
                    {rhOpen.commInterne && (
                      <div className="ml-2 space-y-0.5">
                        <LienMenu
                          to="/rh/enquetes"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/enquetes') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                        >
                          <BarChart2 className="h-4 w-4" />
                          <span>Enquêtes internes</span>
                        </LienMenu>
                        <LienMenu
                          to="/rh/evenements"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/evenements') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                        >
                          <PartyPopper className="h-4 w-4 text-pink-500" />
                          <span>Événements</span>
                        </LienMenu>
                        <LienMenu
                          to="/rh/annonces"
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${location.pathname.startsWith('/rh/annonces') ? 'text-blue-700 bg-blue-50 font-medium' : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'}`}
                        >
                          <Bell className="h-4 w-4 text-purple-500" />
                          <span>Annonces</span>
                        </LienMenu>
                      </div>
                    )}

                  </div>
                )}

                {/* ── Sous-menu Courrier (GEC — Gestion Électronique des Courriers) ── */}
                {sidebarOpen && hasSubmenu && isOpen && item.id === 'courrier' && (
                  <div className="ml-8 mt-1 mb-2 space-y-1 border-l-2 border-gray-200 pl-2">
                    {canViewAllCourriers && (
                      <LienMenu
                        to="/courrier"
                        onClick={fermerSiDrawer}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                          location.pathname === '/courrier'
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                        }`}>
                        <Inbox className="h-4 w-4" />
                        <span>Tous les courriers</span>
                      </LienMenu>
                    )}
                    {hasPermission('access_courrier') && (
                      <LienMenu
                        to="/demandes/nouvelle/COURRIER_ENTRANT"
                        onClick={fermerSiDrawer}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                          location.pathname === '/demandes/nouvelle/COURRIER_ENTRANT'
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                        }`}>
                        <Plus className="h-4 w-4" />
                        <span>Nouveau courrier entrant</span>
                      </LienMenu>
                    )}
                    {hasPermission('access_courrier') && (
                      <LienMenu
                        to="/demandes/nouvelle/COURRIER_SORTANT"
                        onClick={fermerSiDrawer}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                          location.pathname === '/demandes/nouvelle/COURRIER_SORTANT'
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                        }`}>
                        <Send className="h-4 w-4" />
                        <span>Nouveau courrier sortant</span>
                      </LienMenu>
                    )}

                    {/* Historique : tous les courriers où l'utilisateur est ou
                        était acteur. Visible pour tout le monde (force le filtre
                        backend par acteur via `?mine=1`). */}
                    <LienMenu
                      to="/courrier?mine=1"
                      onClick={fermerSiDrawer}
                      className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                        location.pathname === '/courrier' && location.search.includes('mine=1')
                          ? 'text-blue-700 bg-blue-50 font-medium'
                          : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                      }`}>
                      <History className="h-4 w-4" />
                      <span>Historique</span>
                    </LienMenu>

                    {/* Types d'opération dynamiques (DevMode) ciblant courrier */}
                    <SidebarDynamicTypes
                      moduleCode="courrier"
                      closeMobile={() => setSidebarOpen(false)} />

                    {/* Lien direct vers la configuration de l'Espace Public
                        de l'entité courante (visible uniquement si l'utilisateur
                        a une entité — quasi tous les users connectés). */}
                    {(user as any)?.entiteId && (
                      <>
                        <div className="border-t border-gray-200 my-2"></div>
                        <LienMenu
                          to={`/admin/entites/${(user as any).entiteId}/espace-public`}
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                            location.pathname.includes('/espace-public')
                              ? 'text-blue-700 bg-blue-50 font-medium'
                              : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}>
                          <Globe className="h-4 w-4" />
                          <span>Espace Public</span>
                        </LienMenu>
                        {/* Contenu de la vitrine (cadres, actualités, galerie…) */}
                        <LienMenu
                          to={`/admin/entites/${(user as any).entiteId}/vitrine-contenu`}
                          onClick={fermerSiDrawer}
                          className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                            location.pathname.includes('/vitrine-contenu')
                              ? 'text-blue-700 bg-blue-50 font-medium'
                              : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                          }`}>
                          <LayoutDashboard className="h-4 w-4" />
                          <span>Contenu vitrine</span>
                        </LienMenu>
                      </>
                    )}
                  </div>
                )}

                {/* ── Sous-menu Société (Associés, Capital, Dividendes) ── */}
                {sidebarOpen && hasSubmenu && isOpen && item.id === 'societe' && (
                  <div className="ml-8 mt-1 mb-2 space-y-1 border-l-2 border-gray-200 pl-2">
                    <LienMenu
                      to="/societe/associes"
                      onClick={fermerSiDrawer}
                      className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                        location.pathname === '/societe/associes'
                          ? 'text-blue-700 bg-blue-50 font-medium'
                          : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                      }`}
                    >
                      <Users className="h-4 w-4" />
                      <span>Associés / Actionnaires</span>
                    </LienMenu>
                    {hasPermission('view_associes') && (
                      <LienMenu
                        to="/societe/capital-social"
                        onClick={fermerSiDrawer}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                          location.pathname === '/societe/capital-social'
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                        }`}
                      >
                        <Coins className="h-4 w-4" />
                        <span>Capital social</span>
                      </LienMenu>
                    )}
                    {hasPermission('view_associes') && (
<LienMenu
                        to="/dividendes/distributions"
                        onClick={fermerSiDrawer}
                        className={`flex items-center space-x-2 p-2 text-sm rounded-lg transition-colors ${
                          location.pathname.startsWith('/dividendes')
                            ? 'text-blue-700 bg-blue-50 font-medium'
                            : '[color:var(--color-sidebar-text,#374151)] hover:bg-[var(--color-sidebar-hover,#eff6ff)] hover:text-blue-700'
                        }`}
                      >
                        <TrendingUp className="h-4 w-4" />
                        <span>Dividendes</span>
                      </LienMenu>
                    )}
                  </div>
                )}

                {/* ── Sous-menu Enquêtes satisfaction ── */}
                {sidebarOpen && hasSubmenu && isOpen && item.id === 'enquetes_satisfaction' && (
                  <div className="ml-8 mt-1 mb-2 space-y-1 border-l-2 border-gray-200 pl-2">
                    <LienMenu
                      to="/crm/enquetes-satisfaction"
                      onClick={fermerSiDrawer}
                      className="flex items-center space-x-2 p-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <List className="h-4 w-4" /><span>Liste des enquêtes</span>
                    </LienMenu>
                    <LienMenu
                      to="/crm/enquetes-satisfaction/bilan"
                      onClick={fermerSiDrawer}
                      className="flex items-center space-x-2 p-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <BarChart3 className="h-4 w-4" /><span>Bilan satisfaction</span>
                    </LienMenu>
                    <LienMenu
                      to="/crm/enquetes-satisfaction/suivi-actions"
                      onClick={fermerSiDrawer}
                      className="flex items-center space-x-2 p-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <CheckCircle className="h-4 w-4" /><span>Suivi des actions</span>
                    </LienMenu>
                  </div>
                )}

              </div>
            );
            }; // fin renderMenuItem

            // ── Modules auto-sidebar (catalogue BD) ─────────────────────────
            // Rendu dans le bloc ERP (cf. plus bas). Liste calculée ici pour
            // pouvoir l'injecter à l'endroit voulu sans dupliquer la logique.
            // Pour CHAQUE module marqué autoSidebar=true dans le catalogue ET
            // auquel l'utilisateur a accès (via son package), on rend un item
            // générique avec sous-menu = SidebarDynamicTypes(moduleCode).
            const CRM_BLOC_ID = 'crm';
            const SECURITE_BLOC_ID = 'securite';
            // rh est exclu car géré en tant qu'item hardcodé avec accordion (renderMenuItem).
            const erpAutoModules = (permissionsLoaded ? autoModules : []).filter(
              m => (!m.superBloc || m.superBloc === 'erp') && m.cle !== 'rh',
            );
            const crmAutoModules = (permissionsLoaded ? autoModules : []).filter(
              m => m.superBloc === 'crm',
            );
            const securiteAutoModules = (permissionsLoaded ? autoModules : []).filter(
              m => m.superBloc === 'securite',
            );

            // Pipeline de filtres commun (droits + tri + rendu).
            const buildAutoModuleNodes = (list: typeof autoModules) =>
              list
              .filter(m => hasModule(m.cle))
            // Filtre par droit utilisateur : un module n'apparaît que si
            // l'utilisateur a le droit `access_<cle>` OU un alias historique.
            // Conventions de nommage tolérées :
            //   - access_<cle> standard (ex: access_courrier, access_projets)
            //   - access_<cle sans préfixe domaine> (ex: btp_chantier →
            //     access_chantier, btp_devis → access_devis_chantier)
            //   - access_<cle>_chantier pour les vieux codes BTP
            //   - droit fourre-tout d'admin module (ex: admin_btp)
            // hasPermission retourne true pour admin/super_admin (cf. AuthContext),
            // donc pas de régression admin.
            .filter(m => {
              const candidats = new Set<string>([
                'access_' + m.cle,
              ]);
              // BTP : modules_catalogue contient 'btp_chantier' / 'btp_devis'
              // mais les droits réels sont access_chantier / access_devis_chantier.
              if (m.cle.startsWith('btp_')) {
                const tronque = m.cle.slice(4);
                candidats.add('access_' + tronque);
                // ex: btp_devis → access_devis_chantier
                if (tronque === 'devis') candidats.add('access_devis_chantier');
                candidats.add('admin_btp');
              }
              // Couvre aussi les catégoriesDroits déclarées sur le module
              if (Array.isArray(m.categoriesDroits)) {
                m.categoriesDroits.forEach(c => {
                  if (c) candidats.add('access_' + c);
                });
              }
              return Array.from(candidats).some(c => hasPermission(c));
            })
            .sort((a, b) => (a.ordre || 0) - (b.ordre || 0))
            .map(m => (
              <SidebarAutoModule
                key={m.cle}
                module={m}
                sidebarOpen={sidebarOpen}
                closeMobile={() => setSidebarOpen(false)}
              />
            ));

            const autoModuleNodes = buildAutoModuleNodes(erpAutoModules);
            const crmAutoModuleNodes = buildAutoModuleNodes(crmAutoModules);
            const securiteAutoModuleNodes = buildAutoModuleNodes(securiteAutoModules);

            // Index id → item visible (gating déjà appliqué dans filteredMenuItems).
            const byId = new Map(filteredMenuItems.map(it => [it.id, it]));

            // En-tête cliquable d'un super-bloc (icône + label + chevron).
            const renderBlocHeader = (bloc: SuperBloc, blocOpen: boolean) => (
              <div
                onClick={() => sidebarOpen && toggleBloc(bloc.id)}
                title={!sidebarOpen ? bloc.label : ''}
                className={`flex items-center justify-between px-3 py-2 mt-2 mb-1 rounded-lg cursor-pointer select-none uppercase tracking-wide text-xs font-bold [color:var(--color-sidebar-muted,#9ca3af)] hover:bg-gray-100 transition-colors ${!sidebarOpen ? 'justify-center' : ''}`}
              >
                <div className="flex items-center space-x-2 min-w-0">
                  <bloc.icon className="h-4 w-4 flex-shrink-0 [color:var(--color-sidebar-muted,#9ca3af)]" />
                  {sidebarOpen && <span className="truncate">{bloc.label}</span>}
                </div>
                {sidebarOpen && (
                  blocOpen
                    ? <ChevronDown className="w-4 h-4 [color:var(--color-sidebar-muted,#9ca3af)] flex-shrink-0" />
                    : <ChevronRight className="w-4 h-4 [color:var(--color-sidebar-muted,#9ca3af)] flex-shrink-0" />
                )}
              </div>
            );

            const dashboardItem = byId.get('dashboard');
            const monEspaceItem = byId.get('monEspace');

            return (
              <>
                {/* Épinglé HAUT (hors bloc) */}
                {dashboardItem && renderMenuItem(dashboardItem)}

                {/* Super-blocs de 1er niveau */}
                {SUPER_BLOCS.map(bloc => {
                  // Items visibles de ce bloc, filtrés par permissions et par recherche.
                  const searchLower = searchQuery.toLowerCase();
                  const matchSearch = (item: MenuItemDef) =>
                    !searchQuery || item.label.toLowerCase().includes(searchLower);
                  const blocItems = filteredMenuItems.filter(it => bloc.itemIds.includes(it.id) && matchSearch(it));
                  const hasAutoModulesHere = bloc.id === ERP_BLOC_ID && autoModuleNodes.length > 0 && !searchQuery;
                  const hasCrmAutoModulesHere = bloc.id === CRM_BLOC_ID && crmAutoModuleNodes.length > 0 && !searchQuery;
                  const hasSecuriteAutoModulesHere = bloc.id === SECURITE_BLOC_ID && securiteAutoModuleNodes.length > 0 && !searchQuery;
                  // Masquer tout le bloc si aucun item visible (et pas d'auto-module pour ERP, CRM ou Sécurité).
                  if (blocItems.length === 0 && !hasAutoModulesHere && !hasCrmAutoModulesHere && !hasSecuriteAutoModulesHere) return null;
                  const blocOpen = searchQuery ? true : (openBlocs[bloc.id] ?? true);
                  return (
                    <div key={`bloc-${bloc.id}`}>
                      {renderBlocHeader(bloc, blocOpen)}
                      {blocOpen && (
                        <div className={sidebarOpen ? 'ml-2 pl-1 border-l border-gray-100' : ''}>
                          {blocItems.map(it => renderMenuItem(it))}
                          {hasAutoModulesHere && autoModuleNodes}
                          {hasCrmAutoModulesHere && crmAutoModuleNodes}
                          {hasSecuriteAutoModulesHere && securiteAutoModuleNodes}
                        </div>
                      )}
                    </div>
                  );
                })}

                {/* Message aucun résultat */}
                {searchQuery && filteredMenuItems.filter(item => item.label.toLowerCase().includes(searchQuery.toLowerCase())).length === 0 && (
                  <p className="text-xs [color:var(--color-sidebar-muted,#9ca3af)] text-center py-4 px-2">Aucun résultat pour « {searchQuery} »</p>
                )}

                {/* Épinglé BAS (hors bloc) */}
                {monEspaceItem && (
                  <div className="mt-2 pt-2 border-t border-gray-100">
                    {renderMenuItem(monEspaceItem)}
                  </div>
                )}
              </>
            );
          })()}

          {/* Indicateur de chargement */}
          {!permissionsLoaded && (
            <div className="text-center py-4">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-xs [color:var(--color-sidebar-muted,#9ca3af)] mt-2">{t('sidebar.loadingPermissions')}</p>
            </div>
          )}
        </nav>

        {/* ── Bas de sidebar : statut session uniquement ── */}
        <div className="border-t px-3 py-2 bg-gray-50 flex-shrink-0">
          {sidebarOpen ? (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${permissionsLoaded ? 'bg-green-500 animate-pulse' : 'bg-amber-400'}`} />
                <span className={`text-xs ${permissionsLoaded ? 'text-green-700' : 'text-amber-700'}`}>
                  {permissionsLoaded ? t('sidebar.sessionActive') : t('sidebar.loading')}
                </span>
              </div>
              <span className="text-xs [color:var(--color-sidebar-muted,#9ca3af)]">{filteredMenuItems.length} {t('sidebar.menus')}</span>
            </div>
          ) : (
            <div className="flex justify-center">
              <div className={`w-2 h-2 rounded-full ${permissionsLoaded ? 'bg-green-500 animate-pulse' : 'bg-amber-400'}`} />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Sidebar;